// Centralized data file for all site content

export interface MegaMenuItem {
  id: string;
  title: string;
  description?: string;
  icon?: string;
  subServices?: string[];
  buttonText?: string;
  buttonLink?: string;
}

export interface MegaMenuCategory {
  id: string;
  title: string;
  description: string;
  items: MegaMenuItem[];
}

export const siteData = {
  // Hero Section
  hero: {
    tag: {
      icon: "sparkle",
      text: "Custom Software Development Agency",
      bgColor: "light",
      textColor: "primary",
      borderColor: "secondary",
    },
    title: "Crafting Scalable, High-Impact Digital Solutions",
    description:
      "As a leading software development agency, we deliver fast, secure, and scalable custom solutions for startups and enterprises. From web to mobile and cloud, we turn complex ideas into high-performance digital products.      ",
    buttons: [
      {
        text: "Book Free Consultation",
        bgColor: "primary",
        textColor: "light",
        className:
          "px-6 py-3 text-sm font-medium hover:bg-primary/90 transition-colors",
      },
      {
        text: "Contact us",
        type: "secondary",
        className:
          "flex items-center gap-2 px-6 py-3 border border-border text-foreground font-medium rounded-md hover:bg-light/50 transition-colors text-sm cursor-pointer",
      },
    ],
  },

  // Companies Section
  companies: {
    title: "Trusted by leading companies worldwide",
    logos: [
      { name: "Company 1", src: "/company1.svg" },
      { name: "Company 2", src: "/company2.svg" },
      { name: "Company 3", src: "/company3.svg" },
      { name: "Company 4", src: "/company4.svg" },
      { name: "Company 5", src: "/company5.svg" },
      { name: "Company 6", src: "/company6.svg" },
    ],
  },

  // Features Section
  features: {
    tag: {
      text: "Our strategies",
    },
    title: "Our Top Notch Services",
    description:
      "Browse our custom software solutions built for web, mobile, and cloud — designed to scale and solve real business challenges. Each project reflects our focus on innovation and results.",
    buttons: [
      {
        text: "Book Free Consultation",
      },
      {
        text: "Contact us",
      },
    ],
    features: [
      {
        id: 1,
        title: "Application Development",
        description:
          "We develop custom web, mobile, and cloud solutions with React, Node.js, and AI. Partner with our expert team to turn complex ideas into secure, high‑performance apps that scale—and outrank the competition",
        icon: "CodeIcon",
      },
      {
        id: 2,
        title: "AI, LLM, Integrations",
        description:
          "We integrate GPT‑4o, Claude 3, and bespoke LLMs into web and mobile apps—automating support, analytics, and content at scale. Our AI engineers fine‑tune models on your domain data and deploy them on secure cloud infra, boosting ROI for brands across Pakistan, the GCC, and the US.",
        icon: "ArtificialIntelligenceIcon",
      },
      {
        id: 3,
        title: "POC Development",
        description:
          "ZySoftec delivers lightning‑fast Proof‑of‑Concept (PoC) development, turning rough ideas into clickable prototypes within weeks. Our full‑stack team uses React, Node.js, and AI to validate feasibility, attract investors",
        icon: "TargetIcon",
      },
      {
        id: 4,
        title: "Product Design",
        description:
          "ZySoftec turns ideas into pixel‑perfect digital products with award‑winning UI/UX and product design services. Our Figma‑powered workflows craft intuitive, conversion‑focused interfaces that delight users and drive growth for brands",
        icon: "UXConsultingIcon",
      },
      {
        id: 5,
        title: "Enterprise Development",
        description:
          "ZySoftec delivers end‑to‑end enterprise software development, building modular, cloud‑ready ERP and CRM solutions that streamline operations, sales, and finance.",
        icon: "NetworkIcon",
      },
    ],
  },

  // Portfolio Section
  portfolio: {
    tag: {
      text: "Applications",
    },
    title: "Our Works",
    description:
      "Browse our portfolio to see 120+ high‑impact launches—AI‑powered fintech dashboards, multilingual e‑commerce sites, and cloud‑native health‑tech platforms built with React, Next.js, and Node.js. Every project pairs pixel‑perfect UI/UX with measurable ROI for brands.",
    applications: [
      {
        id: "web-development-portfolio",
        title: "Web Development",
        description:
          "We build responsive, secure, and scalable web applications tailored to your goals. Using modern tech stacks like MERN and Next.js, our web solutions are optimized for performance, usability, and future growth.",
        icon: "DataScienceIcon",
        tags: ["MERN", "Full Stack", "Scalable"],
      },
      {
        id: "mobile-development-portfolio",
        title: "Mobile App Development",
        description:
          "From iOS to Android and cross-platform apps, we develop mobile solutions that deliver smooth, high-performance user experiences. Whether it’s a startup MVP or enterprise-grade app, we’ve got you covered — from design to deployment.",
        icon: "NetworkIcon",
        tags: ["Cross-platform", "MVP", "React Native"],
      },
      {
        id: "ui-ux-design-portfolio",
        title: "UI UX Design",
        description:
          "We craft intuitive and user-focused UI/UX designs that enhance engagement and usability across web and mobile platforms. Our design approach ensures seamless navigation, responsive layouts, and visually compelling interfaces.",
        icon: "DigitalEnterpriseIcon",
        tags: ["UI/UX", "Figma", "Responsive"],
      },
      {
        id: "cms-portfolio",
        title: "CMS",
        description:
          "We develop powerful CMS solutions using WordPress and Shopify, tailored for seamless content management and high-performance websites. Our CMS services focus on scalability, SEO optimization, and user-friendly interfaces to drive engagement and business growth.",
        icon: "DevOpsIcon",
        tags: ["Wordpress", "Shopify", "CMS"],
      },
      {
        id: "ai-llm-integrations-portfolio",
        title: "AI, LLM, AI Integrations",
        description:
          "Leverage the power of AI and large language models to enhance automation, personalization, and decision-making. We integrate cutting-edge AI tools into your software products — from GPT-based chatbots to custom ML pipelines — tailored to your business.",
        icon: "CloudIcon",
        tags: ["AI", "Chatbot", "ML"],
      },
      {
        id: "logo-branding-portfolio",
        title: "Logo & Branding",
        description:
          "We craft impactful logo and branding solutions that strengthen your visual identity and resonate with your target audience. Our designs are rooted in strategy, creativity, and consistency—helping your brand stand out across all digital and print platforms.",
        icon: "CustomerExperienceIcon",
        tags: ["Logo", "Branding", "Identity"],
      },
    ],
  },

  // FAQ Section
  faq: {
    tag: {
      icon: "sparkle",
      text: "FAQ",
      bgColor: "light",
      textColor: "foreground",
      borderColor: "primary",
    },
    title: "Frequently asked questions",
    description:
      "Explore our frequently asked questions to learn more about ZySoftec's development services, security practices, integration capabilities, and more.",
    items: [
      {
        id: 0,
        question: "Which technologies do you specialize in?",
        answer:
          "Our full‑stack team excels with React, Next.js, React Native, Node.js, Python, Wordpress, Shopify, PostgreSQL, and microservices on AWS, Azure, and GCP, plus cutting‑edge large language models like GPT‑4o and Claude 3.",
      },
      {
        id: 1,
        question: "What is your average project timeline?",
        answer:
          "Proof‑of‑Concepts can be done in 2‑3 weeks, MVPs in 3-6 weeks, and enterprise platforms depending on features, integrations, and stakeholder feedback cycles.",
      },
      {
        id: 2,
        question: "Do you use Agile or Waterfall?",
        answer:
          "We favor Agile Scrum with two‑week sprints, clear deliverables, and continuous integration, but can adapt to Waterfall or hybrid models when corporate governance requires it.",
      },
      {
        id: 3,
        question: "Can you take over an existing codebase?",
        answer:
          "Yes—after a technical audit, we can refactor legacy code, boost performance, migrate to the cloud, or add new features without disrupting live operations.",
      },
      {
        id: 4,
        question: "Which industries do you serve?",
        answer:
          "ZySoftec has delivered solutions for e‑commerce, fintech, health‑tech, logistics, real estate, legal tech, and automotive, adapting domain best practices to each engagement.",
      },
      {
        id: 5,
        question: "How do we get started?",
        answer:
          "Book a free consultation via hello@zysoftec.com or +92 3175682252. We'll discuss goals, draft a proposal, and kick off your software project within days.",
      },
    ],
  },

  // Reviews Section
  reviews: {
    tag: {
      icon: "sparkle",
      text: "The ZySoftec Experience",
      bgColor: "light",
      textColor: "primary",
      borderColor: "secondary",
    },
    title: "The ZySoftec Experience",
    description:
      "At ZySoftec, we pride ourselves on delivering top-notch business. But don't just take our word for it. Hear what our satisfied users have to say.",
    testimonials: [
      {
        id: 1,
        text: "Zysoftec truly EXCELS in software development, delivering bug-free code with unmatched expertise and exceeding all expectations! Their politeness and proactive communication made the experience seamless, and I really appreciated their honesty and dedication. One of the BEST DEVELOPERS you'll encounter 100% recommend! 🙌",
        author: "Trumely",
        title: "CEO",
        avatar: "T",
      },
      {
        id: 2,
        text: "Zill and her team Mohamed and the others, are very proffesional, very patinece and they just go as far as we wanted to make things as we requested and even better, We gave our needs with some details, and they made it true with their talent. We apperciate their clean work.",
        author: "Ali",
        title: "Libya",
        avatar: "A",
      },
      {
        id: 3,
        text: "Zysoftec delivered an EXCEPTIONAL experience in software development, surpassing all expectations with their professionalism and flawless work. The team's quick responsiveness, COOPERATIVE spirit, and polite demeanor made collaboration a real pleasure. Thank you, Zilly and the team, for your unwavering commitment and dedication 🙌 — I look forward to working with you again!",
        author: "Alaa",
        title: "Egypt",
        avatar: "A",
      },
      {
        id: 4,
        text: "Zysoftec delivered top-notch professional work in software development. Working with them was a breeze due to their cooperative spirit, quick responsiveness, and fluent communication. Highly recommend! 👍",
        author: "Abhijeeth",
        title: "India",
        avatar: "A",
      },
      {
        id: 5,
        text: "Great work working with the team. They helped me complete the first stage of the project and all went smoothly. Would recommended the team for anyone looking for a hardworking front end and back end development team who will also give you great advise on what to do next. Very happy with the outcome!",
        author: "Eddie",
        title: "United Kingdom",
        avatar: "E",
      },
    ],
  },

  // CTA Section
  cta: {
    title: "Unlock the Next Generation of Software Development with ZySoftec.",
    description:
      "At ZySoftec, we partner with you to deliver fast, reliable, and cutting‑edge web and mobile applications and more.",
    button: {
      text: "Book Free Consultation",
      bgColor: "primary",
      textColor: "light",
      className:
        "px-8 py-4 text-base font-medium hover:bg-primary/90 transition-colors relative",
    },
  },

  // Footer Section
  footer: {
    logo: {
      text: "ZySoftec",
      icon: "sparkle",
    },
    links: [
      {
        title: "About ZySoftec",
        links: [
          { name: "Company Overview", href: "/about-zysoftec" },
          { name: "Careers", href: "/contact" },
          // { name: "Press & Media", href: "#" },
          { name: "Products", href: "/products" },
        ],
      },
      {
        title: "Resources",
        links: [
          // { name: "Blog", href: "/blogs" },
          // { name: "Help Center", href: "#" },
          // { name: "Webinars & Events", href: "#" },
          { name: "Case Studies", href: "/case-studies" },
          { name: "Contact Us", href: "/contact" },
        ],
      },
      // {
      //   title: "Support & Contact",
      //   links: [
      //     { name: "Contact Us", href: "#" },
      //     // { name: "Technical Support", href: "#" },
      //     // { name: "Feedback", href: "#" },
      //     // { name: "Community Forum", href: "#" },
      //   ],
      // },
      {
        title: "Connect",
        links: [
          {
            name: "Instagram",
            href: "https://www.instagram.com/zysoftec?igshid=NTc4MTIwNjQ2YQ%3D%3D",
            icon: "instagram",
          },
          {
            name: "Facebook",
            href: "https://www.facebook.com/ZySoftec?mibextid=ZbWKwL",
            icon: "facebook",
          },
          // { name: "Twitter", href: "#", icon: "twitter" },
          {
            name: "LinkedIn",
            href: "https://www.linkedin.com/company/zysoftec",
            icon: "linkedin",
          },
        ],
      },
    ],
    copyright: "©2026 ZySoftec All rights reserved.",
    legal: [
      { name: "Terms of use", href: "/terms-of-use" },
      { name: "Privacy policy", href: "/privacy-policy" },
      { name: "Refund Policy", href: "/refund-policy" },
    ],
  },

  // World Map Data
  worldMap: {
    pinColor: "#263a8d",
    points: [
      { lat: 2.0, lng: -90.0, label: "Mexico", projects: 5 },
      { lat: 20.0, lng: -98.0, label: "USA", projects: 12 },
      { lat: 38.0, lng: -85.0, label: "Canada", projects: 9 },
      { lat: -40.0, lng: -45.0, label: "Brazil", projects: 45 },
      { lat: -5.0, lng: 45.0, label: "Pakistan", projects: 100 },
      { lat: -12.0, lng: 52.0, label: "India", projects: 126 },
      { lat: -10.0, lng: 33.0, label: "Dubai", projects: 84 },
      { lat: 20.0, lng: -10.0, label: "UK", projects: 26 },
      { lat: 5.0, lng: -10.0, label: "Spain", projects: 100 },
      { lat: -18.0, lng: -20.0, label: "Senegal", projects: 51 },
      { lat: -8.0, lng: 15.0, label: "Egypt", projects: 33 },
      { lat: -8.0, lng: 5.0, label: "Libya", projects: 92 },
      { lat: -22.0, lng: -3.0, label: "Nigeria", projects: 45 },

      { lat: -3.0, lng: 20.0, label: "Jordan", projects: 68 },
    ],
  },

  // Case Study Mega Menu
  caseStudyMegaMenu: {
    categories: [
      {
        id: "case study",
        title: "Case Study",
        description:
          "Measurable growth, and elevate user experiences in every case study.",
        items: [
          {
            id: "org-update",
            title: "OrgUpdate",
            description:
              "Data visualization platform for recruitment, workforce planning, research and insights departments.",
            icon: "CodeIcon",
          },
          {
            id: "simple-numbers",
            title: "Simple Numbers",
            description:
              "Book keeping simplified - Client's financial information at fingertips, a dashboard for all.",
            icon: "TargetIcon",
          },
          {
            id: "personalized",
            title: "ShirtBay",
            description:
              "Personalize gifts by changing real-time graphics and printing them on item of your choice.",
            icon: "DeliveryIcon",
          },
          {
            id: "nujoom",
            title: "Nujoom AI",
            description:
              "AI Solutions involving QnA Engine, Speed and analyzer engine.",
            icon: "NetworkIcon",
            // subServices: ["ERP consulting", "CRM consulting"],
          },
          {
            id: "seller-metrices",
            title: "Seller Metrics",
            description:
              "Biggest seller marketplace platform developed to track sales, finances, incentory.",
            icon: "UpgradeIcon",
          },
          {
            id: "cherrysprites",
            title: "Cherrysprites",
            description: "Advanced reminder system.",
            icon: "CloudIcon",
            // subServices: ["Cloud migration"],
          },
        ],
      },
      {
        id: "data-ai",
        title: "Software Development - Portfolio",
        description:
          "Building scalable applications that drive business success.",
        items: [
          {
            id: "ai-solutions-advisor",
            title: "Saas",
            description:
              "Tax Consultation Platform build to onboard clients, send build-in offers, track within system, get paid.",
            icon: "AISolutionsIcon",
            // buttonText: "Start the assessment",
            // buttonLink: "#",
          },
          {
            id: "data-science",
            title: "AI",
            description:
              "Elevate your software with AI-powered intelligence and automation",
            icon: "DataScienceIcon",
          },
          {
            id: "data-engineering",
            title: "E-commerce",
            description: "ZySoftec builds numerous application and pos systems",
            icon: "DataEngineeringIcon",
            
          },
          {
            id: "mlops",
            title: "CRM/ERP",
            description:
              "Many ERP, CRM, web based portals are developed by us",
            icon: "MLOpsIcon",
          },
          {
            id: "artificial-intelligence",
            title: "Mobile Apps",
            description: "Cross-platforms apps are build using react native",
            icon: "ArtificialIntelligenceIcon",
          },
          {
            id: "intelligent-automation",
            title: "Multi-vendor",
            description: "Salon services, agent/vehicle listing, meat vendor platforms",
            icon: "IntelligentAutomationIcon",
            // subServices: ["Intelligent document processing"],
          },
          // {
          //   id: "data-engineering-description",
          //   title: "Landing Pages",
          //   description:
          //     "Realize your data's full potential with our enterprise-grade data engineering services.",
          //   icon: "DataEngineeringIcon",
          // },
          {
            id: "llmops",
            title: "Education",
            description:
              "Dtaa ectraction, HR, Hiring, courses platforms",
            icon: "LLMOpsIcon",
          },
        ],
      },
      {
        id: "expertise",
        title: " Design - Portfolio",
        description:
          "Transforming ideas into cohesive, memorable digital experiences.",
        items: [
          {
            id: "devops",
            title: "Website UI/UX Design",
            description:
              "Crafting intuitive, visually engaging websites that seamlessly guide users and elevate brand experiences.",
            icon: "DevOpsIcon",
          },
          {
            id: "vr-ar-mr",
            title: "App UI/UX design",
            description:
              "Crafting intuitive, user‑focused mobile app interfaces that blend seamless functionality with engaging visuals.",
            icon: "VRHeadsetIcon",
          },
          {
            id: "iot",
            title: "Dashboard UI/UX",
            description:
              "Designing modular, user‑friendly dashboards and ERP panels that surface key metrics.",
            icon: "IoTIcon",
          },
          // {
          //   id: "market-research",
          //   title: "Creatives",
          //   description:
          //     "Evaluate your business landscape to capitalise on promising niches and get ahead of the competition.",
          //   icon: "MarketResearchIcon",
          // },
          // {
          //   id: "customer-experience",
          //   title: "Logo & Branding",
          //   description:
          //     "Refine every customer interaction to enhance satisfaction and drive sustainable revenue growth.",
          //   icon: "CustomerExperienceIcon",
          // },
        ],
      },
    ],
  },

  // Services Mega Menu
  servicesMegaMenu: {
    categories: [
      {
        id: "development",
        title: "Development Services",
        description: "Custom software solutions for your business needs.",
        items: [
          {
            id: "web-development",
            title: " Web Development",
            description:
              "Modern web applications built with React, Next.js, and Node.js.",
            icon: "CodeIcon",
            // subServices: [
            //   "Frontend Development",
            //   "Backend Development",
            //   "Full-Stack Development",
            // ],
          },
          {
            id: "mobile-development",
            title: "Mobile Development",
            description: "Native and cross-platform mobile applications.",
            icon: "DevOpsIcon",
            // subServices: [
            //   "iOS Development",
            //   "Android Development",
            //   "React Native",
            // ],
          },
          {
            id: "ai-integration",
            title: "AI & ML Integration",
            description:
              "Integrate AI and machine learning into your applications.",
            icon: "AISolutionsIcon",
            // subServices: [
            //   "GPT Integration",
            //   "Custom AI Models",
            //   "ML Pipelines",
            // ],
          },
          {
            id: "cloud-services",
            title: "Cloud Services",
            description: "Cloud-native solutions and infrastructure.",
            icon: "CloudIcon",
            // subServices: ["AWS", "Azure", "Google Cloud", "DevOps"],
          },
          {
            id: "enterprise-applications",
            title: "Enterprise Applications",
            description: "Scalable, secure solutions for large organizations.",
            icon: "CloudIcon",
            // subServices: ["AWS", "Azure", "Google Cloud", "DevOps"],
          },
          {
            id: "cms",
            title: "CMS",
            description:
              "Custom content management systems for efficient publishing.",
            icon: "CloudIcon",
            // subServices: ["AWS", "Azure", "Google Cloud", "DevOps"],
          },
          {
            id: "landing-page",
            title: "Landing Page",
            description:
              "High-converting, responsive landing pages for campaigns.",
            icon: "CloudIcon",
            // subServices: ["AWS", "Azure", "Google Cloud", "DevOps"],
          },
          {
            id: "pwa",
            title: "PWA",
            description:
              "Progressive web apps with offline capabilities and native-like experience.",
            icon: "CloudIcon",
            // subServices: ["AWS", "Azure", "Google Cloud", "DevOps"],
          },
        ],
      },
      {
        id: "consulting",
        title: "Consulting & Optimization",
        description: "Strategic guidance for digital transformation.",
        items: [
          {
            id: "quality-assurance",
            title: "Quality Assurance",
            description:
              "Comprehensive testing and quality control for reliable software.",
            icon: "DigitalEnterpriseIcon",
            // subServices: [
            //   "Automated Testing",
            //   "Manual Testing",
            //   "Performance Testing",
            // ],
          },
          {
            id: "product-consultation",
            title: "Product Consultation",
            description:
              "Strategic guidance to optimize your product development process.",
            icon: "DataScienceIcon",
            // subServices: [
            //   "Product Strategy",
            //   "Market Analysis",
            //   "Feature Prioritization",
            // ],
          },
          {
            id: "bug-fixing",
            title: "Bug Fixing",
            description:
              "Identify and resolve software issues to improve stability.",
            icon: "TargetIcon",
            // subServices: [
            //   "Error Diagnosis",
            //   "Code Debugging",
            //   "Regression Testing",
            // ],
          },
          {
            id: "code-optimization",
            title: "Speed Optimization",
            description:
              "Enhance performance and loading times of your applications.",
            icon: "SpeedIcon",
            // subServices: [
            //   "Performance Auditing",
            //   "Code Refactoring",
            //   "Resource Optimization",
            // ],
          },
          {
            id: "on-page-seo",
            title: "Technical & On-Page SEO",
            description:
              "Improve search visibility and organic traffic to your website.",
            icon: "SearchIcon",
            // subServices: [
            //   "SEO Auditing",
            //   "Keyword Optimization",
            //   "Schema Markup",
            // ],
          },
        ],
      },
      {
        id: "design",
        title: "Design Services",
        description: "Beautiful and functional design solutions.",
        items: [
          {
            id: "ui-ux-design",
            title: "UI/UX Design",
            description:
              "User-centered design that drives engagement and conversion.",
            icon: "UXConsultingIcon",
            // subServices: [
            //   "User Research",
            //   "Wireframing",
            //   "Prototyping",
            //   "User Testing",
            // ],
          },
          {
            id: "brand-design",
            title: "Logo & Branding",
            description:
              "Create a memorable brand identity that resonates with your audience.",
            icon: "CustomerExperienceIcon",
            // subServices: [
            //   "Logo Design",
            //   "Brand Guidelines",
            //   "Marketing Materials",
            // ],
          },
          {
            id: "product-design",
            title: "Product Design",
            description: "End-to-end product design from concept to launch.",
            icon: "IoTIcon",
            // subServices: [
            //   "Digital Product Design",
            //   "Design Systems",
            //   "SaaS UI/UX",
            //   "Mobile App Design"
            // ],
          },
          {
            id: "pitch-deck",
            title: "Pitch Deck Design",
            description:
              "Compelling presentations that impress investors and stakeholders.",
            icon: "PresentationIcon",
            // subServices: [
            //   "Branded Slide Decks",
            //   "Custom Infographics",
            //   "Strategic Structure",
            //   "Investor Presentations"
            // ],
          },
          {
            id: "social-media-posts",
            title: "Social Media Creatives",
            description:
              "Engaging visual content optimized for all social platforms.",
            icon: "SocialMediaIcon",
            // subServices: [
            //   "Platform-Specific Posts",
            //   "Carousel Graphics",
            //   "Campaign Designs",
            //   "Reusable Templates"
            // ],
          },
        ],
      },
    ],
  },

  // Industry Mega Menu
  industryMegaMenu: {
    categories: [
      {
        id: "fintech",
        title: "All Industries",
        description: "Innovative solutions for the financial sector.",
        items: [
          {
            id: "banking-solutions",
            title: "Healthcare",
            description:
              "Digital banking platforms and financial management tools.",
            icon: "CodeIcon",
          },
          {
            id: "investment-platforms",
            title: "Ecommerce",
            description: "Trading and investment management platforms.",
            icon: "TargetIcon",
          },
          {
            id: "insurance-tech",
            title: "IT",
            description:
              "Modern insurance platforms and risk assessment tools.",
            icon: "NetworkIcon",
          },
          {
            id: "finance",
            title: "Finance",
            description:
              "Modern insurance platforms and risk assessment tools.",
            icon: "NetworkIcon",
          },
          {
            id: "education",
            title: "Education",
            description:
              "Modern insurance platforms and risk assessment tools.",
            icon: "NetworkIcon",
          },
          {
            id: "multivendor-marketplaces",
            title: "Marketplaces",
            description:
              "Platforms facilitating multi-seller commerce with real-time order and inventory management.",
            icon: "CartIcon",
          },
          {
            id: "nature-travel",
            title: "Nature & Travel",
            description:
              "Solutions for eco-tourism, travel planning, and location-based exploration.",
            icon: "GlobeIcon",
          },
          {
            id: "crm-erp-solutions",
            title: "CRM",
            description:
              "Tools to streamline customer relationship and enterprise resource planning.",
            icon: "SettingsIcon",
          },
          {
            id: "real-estate",
            title: "Real Estate",
            description:
              "Property listing, virtual tours, and smart transaction systems for real estate businesses.",
            icon: "HomeIcon",
          },
          {
            id: "logistics-supply-chain",
            title: "Supply Chain",
            description:
              "Tracking, routing, and automation tools for logistics and delivery services.",
            icon: "TruckIcon",
          },
          {
            id: "food-restaurant-tech",
            title: "Food Industry",
            description:
              "Ordering, delivery, and kitchen management systems for modern eateries.",
            icon: "UtensilsIcon",
          },
          {
            id: "entertainment-media",
            title: "Social Media",
            description:
              "Streaming, content management, and fan engagement platforms.",
            icon: "VideoIcon",
          },
          {
            id: "event-management",
            title: "Event Management",
            description:
              "Ticketing, scheduling, and attendee engagement platforms for events.",
            icon: "CalendarIcon",
          },
          {
            id: "cybersecurity",
            title: "Cybersecurity",
            description:
              "Solutions to safeguard data, manage access, and monitor digital threats.",
            icon: "ShieldIcon",
          },
          {
            id: "ai-ml",
            title: "AI & ML",
            description:
              "Intelligent systems for predictions, automation, and data analysis.",
            icon: "BrainIcon",
          },
        ],
      },
    ],
  },
};

// Modal Data Types
export interface ModalLink {
  text: string;
  url: string;
}

export interface ModalButton {
  text: string;
  action?: () => void;
  variant?: "primary" | "secondary";
}

export interface ModalBullet {
  text: string;
  links?: ModalLink[];
}

export interface ModalParagraph {
  text: string;
  isBold?: boolean;
  bullets?: ModalBullet[];
  links?: ModalLink[];
  buttons?: ModalButton[];
}

export interface ModalSection {
  title?: string;
  paragraphs: ModalParagraph[];
  images?: string[];
  layout?: "text-only" | "text-image" | "image-text" | "text-top" | "image-top";
}

export interface ModalData {
  category?: string;
  title: string;
  subtitle?: string;
  sections: ModalSection[];
}

// Modal Data for Different Services and Sections
export const modalDataMap: { [key: string]: ModalData } = {
  // Services Mega Menu Modals
  "app-development": {
    category: "Software Development",
    title: "Custom App Development",
    subtitle: "Building scalable applications that drive business success.",
    sections: [
      {
        title: "What We Offer",
        paragraphs: [
          {
            text: "We specialize in creating custom applications tailored to your business needs. From mobile apps to enterprise solutions, we deliver high-quality software that scales with your growth.",
            isBold: true,
            bullets: [
              { text: "Native iOS & Android Development" },
              { text: "Cross-platform React Native Apps" },
              { text: "Web Applications & Progressive Web Apps" },
              { text: "Enterprise Software Solutions" },
            ],
          },
        ],
        images: ["/images/truck1.jpg"],
        layout: "text-top",
      },
      {
        title: "Our Process",
        paragraphs: [
          {
            text: "We follow an agile development methodology to ensure your project is delivered on time and within budget.",
            bullets: [
              { text: "Requirements Analysis & Planning" },
              { text: "UI/UX Design & Prototyping" },
              { text: "Development & Testing" },
              { text: "Deployment & Maintenance" },
            ],
          },
        ],
        layout: "text-only",
      },
    ],
  },

  "ai-solutions": {
    category: "Artificial Intelligence",
    title: "AI-Powered Solutions",
    subtitle: "Leveraging cutting-edge AI to transform your business.",
    sections: [
      {
        title: "AI Capabilities",
        paragraphs: [
          {
            text: "Our AI solutions help businesses automate processes, gain insights from data, and create intelligent user experiences.",
            isBold: true,
            bullets: [
              { text: "Machine Learning Models" },
              { text: "Natural Language Processing" },
              { text: "Computer Vision Solutions" },
              { text: "Predictive Analytics" },
            ],
          },
        ],
        images: ["/images/truck2.jpg"],
        layout: "image-top",
      },
    ],
  },

  "web-development": {
    category: "Web Development",
    title: "Modern Web Solutions",
    subtitle: "Creating responsive, fast, and scalable web applications.",
    sections: [
      {
        title: "Technologies We Use",
        paragraphs: [
          {
            text: "At ZYSOFTEC, we specialize in building full-stack web applications using the advanced MERN stack—MongoDB, Express.js, React.js, Next.js, and Node.js. This modern JavaScript-based technology stack allows us to develop dynamic, scalable, and high-performance web platforms from the frontend to the backend.",
          },
          {
            text: "Our web development services are tailored for startups, SMEs, and enterprises looking to build powerful digital solutions. We offer:",
            bullets: [
              { text: "Responsive user interfaces using React.js and Next.js" },
              { text: "Backend APIs and logic with Node.js and Express" },
              {
                text: "Real-time data handling, role-based access control, and advanced authentication",
              },
              {
                text: "Integration with third-party services, payment gateways, and CRMs",
              },
              { text: "Optimized SEO structure and blazing-fast performance" },
            ],
          },
          {
            text: "Whether you need a custom web portal, SaaS product, or internal admin dashboard, we deliver solutions that are reliable, maintainable, and ready for future scaling.",
          },
        ],
        // images: ["/images/truck3.jpg"],
        layout: "text-top",
      },
    ],
  },

  "mobile-development": {
    category: "Mobile Development",
    title: "Custom Mobile App Development",
    subtitle: "Building native and cross-platform mobile applications.",
    sections: [
      {
        title: "What We Offer",
        paragraphs: [
          {
            text: "We develop custom mobile applications that blend performance with beautiful design—ensuring seamless user experiences across all devices. Whether you're launching an MVP or expanding an existing product, we build apps that engage users and support your business goals.",
          },
          {
            text: "Technologies we use include:",
            bullets: [
              { text: "React Native and Flutter for cross-platform apps" },
              {
                text: "Cloud-based APIs, real-time sync, push notifications, and offline support",
              },
              {
                text: "Firebase, AWS Amplify, or custom backends for app logic and data",
              },
              {
                text: "User authentication, secure data handling, and third-party SDKs",
              },
            ],
          },
          {
            text: "From productivity tools to e-commerce, we create apps that are user-centric, secure, and optimized for app store visibility and retention.",
          },
        ],
        // images: ["/images/truck1.jpg"],
        layout: "text-top",
      },
    ],
  },

  "ai-integration": {
    category: "AI Integration",
    title: "AI-Powered Solutions",
    subtitle: "Leveraging cutting-edge AI to transform your business.",
    sections: [
      {
        title: "AI Capabilities",
        paragraphs: [
          {
            text: "Bring intelligence into your business operations with our custom AI and Machine Learning solutions. At ZYSOFTEC, we help you harness data to automate decision-making, enhance personalization, and unlock new insights.",
          },
          {
            text: "Our AI/ML services include:",
            bullets: [
              {
                text: "Natural Language Processing (NLP) for chatbots, virtual assistants, and smart search",
              },
              {
                text: "Computer vision for facial recognition, object detection, and image analysis",
              },
              {
                text: "Predictive analytics for demand forecasting, behavior tracking, and customer insights",
              },
              {
                text: "Recommendation engines for e-commerce and content personalization",
              },
              {
                text: "AI workflow automation for repetitive tasks and smart triggers",
              },
            ],
          },
          {
            text: "We embed AI into your existing systems or build standalone solutions, ensuring scalability, ethical AI use, and seamless integration with your business processes.",
          },
        ],
      },
    ],
  },

  "cloud-services": {
    category: "Cloud Services",
    title: "Cloud Services & DevOps",
    subtitle:
      "Building scalable, secure, and cost-effective cloud infrastructure.",
    sections: [
      {
        title: "Cloud Capabilities",
        paragraphs: [
          {
            text: "ZYSOFTEC empowers organizations to scale faster and smarter through cloud infrastructure and DevOps automation. Our cloud engineers design, deploy, and optimize your architecture across the industry’s top platforms.",
          },
          {
            text: "We offer:",
            bullets: [
              {
                text: "Cloud-native development and deployment on AWS, Google Cloud, and Azure",
              },
              {
                text: "CI/CD pipelines, Docker, Kubernetes, and Infrastructure as Code (IaC)",
              },
              {
                text: "Serverless architectures, scalable databases, and storage solutions",
              },
              { text: "Cloud migration services from legacy systems" },
              { text: "Monitoring, logging, and disaster recovery plans" },
            ],
          },
          {
            text: "Our DevOps practices improve release velocity, reduce downtime, and enable agile scaling for both product teams and enterprises.",
          },
        ],
      },
    ],
  },

  "enterprise-applications": {
    category: "Enterprise Applications",
    title: "Enterprise Applications (CRM & ERP Systems)",
    subtitle:
      "Building scalable, secure, and cost-effective cloud infrastructure.",
    sections: [
      {
        title: "Enterprise Capabilities",
        paragraphs: [
          {
            text: "We design and develop fully customized CRM and ERP applications tailored to your internal operations. Whether you're a growing business or a large enterprise, our software centralizes your workflows and reduces manual inefficiencies.",
          },
          {
            text: "Key modules we develop include:",
            bullets: [
              {
                text: "CRM: Lead management, sales tracking, customer support, communication logs",
              },
              {
                text: "ERP: HR management, finance, payroll, procurement, inventory, and asset control",
              },
              {
                text: "Custom reporting, dashboards, and performance metrics",
              },
              {
                text: "User access control, workflow automation, and notification systems",
              },
              {
                text: "Integration with payment gateways, email tools, and third-party platforms",
              },
            ],
          },
          {
            text: "Our enterprise systems are built for data security, real-time collaboration, and scalable operations—designed to grow with your business.",
          },
        ],
      },
    ],
  },

  cms: {
    category: "CMS",
    title: "CMS Development (WordPress & Shopify)",
    subtitle: "Custom CMS development for WordPress and Shopify.",
    sections: [
      {
        title: "What We Offer",
        paragraphs: [
          {
            text: "ZYSOFTEC delivers custom CMS solutions using WordPress for content-rich websites and Shopify for secure, high-converting eCommerce platforms. Our CMS expertise ensures fast, editable, and SEO-optimized digital experiences.",
          },
          {
            text: "Services include:",
            bullets: [
              { text: "Theme and template customization" },
              { text: "Plugin/app installation and configuration" },
              {
                text: "Product catalog management (Shopify)",
              },
              {
                text: "Multilingual support, blog systems, and dynamic content sections",
              },
              {
                text: "Speed and performance optimization",
              },
              {
                text: "Schema markup, metadata, and SEO-friendly architecture",
              },
            ],
          },
          {
            text: "Whether you're running a personal blog, corporate site, or eCommerce store, we provide a smooth backend experience and mobile-first UI for users.",
          },
        ],
      },
    ],
  },

  "landing-page": {
    category: "Landing Page",
    title: "Landing Page Development",
    subtitle: "Custom landing pages that convert visitors into customers.",
    sections: [
      {
        title: "What We Offer",
        paragraphs: [
          {
            text: "Convert visitors into leads and buyers with high-performance landing pages built for marketing, product launches, and ad campaigns. Every page we build is optimized for clarity, speed, and conversion.",
          },
          {
            text: "Features of our landing page services:",
            bullets: [
              { text: "Responsive layouts and mobile-first design" },
              { text: "A/B testing-ready architecture for marketing teams" },
              { text: "Optimized CTAs, forms, and trust elements" },
              {
                text: "Integration with CRMs, analytics, and email tools",
              },
              {
                text: "Built with Next.js, React, or lightweight frameworks for blazing speed",
              },
            ],
          },
          {
            text: "Whether you're running a Google Ads campaign or building an email funnel, we ensure your landing pages are conversion-focused and search engine-friendly.",
          },
        ],
      },
    ],
  },

  pwa: {
    category: "PWA",
    title: "Progressive Web Apps (PWAs)",
    subtitle: "Building fast, secure, and user-friendly web applications.",
    sections: [
      {
        title: "What We Offer",
        paragraphs: [
          {
            text: "Combine the power of web and mobile in one application with ZYSOFTEC's Progressive Web Apps (PWAs). PWAs work offline, load instantly, and feel like native apps—delivering a superior user experience across all devices.",
          },
          {
            text: "Key features of our PWAs:",
            bullets: [
              {
                text: "Offline capabilities, background sync, and push notifications",
              },
              { text: "Add-to-home screen functionality with full-screen UI" },
              { text: "Lightning-fast loading and SEO-optimized routing" },
              {
                text: "Secure HTTPS delivery and service worker-based caching",
              },
              {
                text: "Cross-device compatibility without needing App Store uploads",
              },
            ],
          },
        ],
      },
    ],
  },

  "quality-assurance": {
    category: "Consulting & Optimization",
    title: "Quality Assurance (QA Testing Services)",
    subtitle: "Ensuring your software is bug-free and performs at its best.",
    sections: [
      {
        title: "What We Offer",
        paragraphs: [
          {
            text: "Deliver bug-free, seamless digital experiences with our end-to-end Quality Assurance services.",
          },
          {
            text: "We perform manual and exploratory testing, UI/UX validation, cross-browser checks, and functional testing to ensure your application performs flawlessly. Our QA process helps catch issues early, reduce downtime, and maintain a consistent user experience across platforms.",
          },
          {
            text: "QA Services include:",
            isBold: true,
            bullets: [
              { text: "Manual Testing" },
              { text: "Regression & Functional Testing" },
              { text: "Usability & Cross-Browser Testing" },
              { text: "Compatibility & Performance Testing" },
            ],
          },
        ],
      },
    ],
  },

  "product-consultation": {
    category: "Consulting & Optimization",
    title: "Product Consultation",
    subtitle: "Helping you build the right product for your business.",
    sections: [
      {
        title: "What We Offer",
        paragraphs: [
          {
            text: "Turn your ideas into scalable digital solutions with our expert product consultation services. We work closely with your team to analyze your current product, identify pain points, and strategize improvements—whether it’s refining features, improving usability, or aligning with your business goals. Our consultants bring real-world tech insights to help you make smarter, faster, and scalable product decisions.",
          },
          {
            text: "We help you with:",
            isBold: true,
            bullets: [
              { text: "Product Roadmap Planning" },
              { text: "Feature Evaluation & Prioritization" },
              { text: "UI/UX Experience Analysis" },
              { text: "Tech Stack Recommendations" },
            ],
          },
        ],
      },
    ],
  },

  "bug-fixing": {
    category: "Consulting & Optimization",
    title: "Bug Fixing & Issue Resolution",
    subtitle: "Keeping your software running smoothly and fixing issues.",
    sections: [
      {
        title: "What We Offer",
        paragraphs: [
          {
            text: "Fix the bugs before they impact your users. Our development and QA teams provide rapid bug fixing to eliminate errors, broken features, and system crashes in both frontend and backend code. We monitor, detect, and resolve technical issues swiftly—improving your software’s stability and performance.",
          },
          {
            text: "What we cover:",
            isBold: true,
            bullets: [
              { text: "UI/UX Glitches" },
              { text: "Backend Logic Errors" },
              { text: "API & Integration Failures" },
              { text: "Cross-Platform Compatibility Issues" },
            ],
          },
        ],
      },
    ],
  },

  "code-optimization": {
    category: "Consulting & Optimization",
    title: "Speed Optimization",
    subtitle: "Boosting your website's performance and user experience.",
    sections: [
      {
        title: "What We Offer",
        paragraphs: [
          {
            text: "Slow websites lead to poor user experience and lower search engine rankings. We offer website and app speed optimization services to boost load times, improve Core Web Vitals, and enhance responsiveness across all devices. By optimizing code, compressing files, reducing server requests, and implementing caching strategies, we help you deliver lightning-fast experiences that retain users and increase conversions.",
          },
          {
            text: "Benefits of speed optimization:",
            isBold: true,
            bullets: [
              { text: "Faster Page Load Times " },
              { text: "Better Mobile Experience" },
              { text: "Improved SEO Rankings" },
              { text: "Reduced Bounce Rates" },
            ],
          },
        ],
      },
    ],
  },

  "on-page-seo": {
    category: "Consulting & Optimization",
    title: "Technical & On-Page SEO",
    subtitle: "Optimizing your website for search engines.",
    sections: [
      {
        title: "What We Offer",
        paragraphs: [
          {
            text: "Drive more organic traffic with our comprehensive SEO optimization services. We combine technical SEO (such as sitemaps, crawlability, structured data, and indexation fixes) with on-page SEO (including keyword optimization, content structure, and meta tags) to make your website search engine-friendly and highly discoverable. Every optimization is tailored to your industry and growth goals.",
          },
          {
            text: "SEO services include:",
            isBold: true,
            bullets: [
              { text: "Site Audits & Technical Fixes" },
              { text: "Meta Tags & Schema Markup" },
              { text: "Content Structuring & Keyword Optimization" },
              { text: "Mobile Optimization & Image SEO" },
            ],
          },
        ],
      },
    ],
  },

  "ui-ux-design": {
    category: "Design Services",
    title: "UI/UX Design",
    subtitle: "Creating intuitive, user-friendly interfaces.",
    sections: [
      {
        title: "What We Offer",
        paragraphs: [
          {
            text: "Deliver seamless user experiences through our user-centered UI/UX design services. From initial research to interactive prototypes, we ensure your digital product is intuitive, engaging, and conversion-optimized across all screen sizes.",
          },
          {
            text: "Our UI/UX design process includes:",
            isBold: true,
            bullets: [
              { text: "User Research & Persona Creation" },
              { text: "Wireframing and Information Architecture" },
              { text: "Interactive Prototyping using Figma" },
              { text: "Usability Testing and Feedback Analysis" },
              { text: "Responsive Interfaces for mobile and web platforms" },
            ],
          },
          {
            text: "Whether you're designing an app, dashboard, or SaaS platform, our UX-first approach ensures higher engagement, reduced bounce rates, and better ROI.",
          },
          {
            text: "Keywords: UI/UX design agency, Figma UI design, user testing, web app design, mobile UX, responsive product UI",
          },
        ],
      },
    ],
  },

  "brand-design": {
    category: "Design Services",
    title: "Logo & Branding",
    subtitle: "Creating a strong, memorable brand identity.",
    sections: [
      {
        title: "What We Offer",
        paragraphs: [
          {
            text: "Build a powerful, consistent brand identity that connects with your audience. Our logo and branding services focus on translating your vision into a visual language that builds trust and recognition across every touchpoint.",
          },
          {
            text: "Branding deliverables include:",
            isBold: true,
            bullets: [
              { text: "Logo Design – Custom, scalable, and meaningful" },
              {
                text: "Brand Guidelines – Typography, color palettes, tone, and usage rules",
              },
              {
                text: "Marketing Collaterals – Social media kits, business cards, packaging, email signatures",
              },
            ],
          },
          {
            text: "Whether you’re a startup or scaling enterprise, we help you define a brand that stands out and stays memorable.",
          },
          {
            text: "Keywords: logo design agency, brand identity, custom branding, brand guidelines, marketing design, startup branding",
          },
        ],
      },
    ],
  },

  "product-design": {
    category: "Design Services",
    title: "Product Design",
    subtitle: "Designing intuitive, user-friendly products.",
    sections: [
      {
        title: "What We Offer",
        paragraphs: [
          {
            text: "We design digital products that solve user problems, align with your business goals, and scale with your technology. Our product design process focuses on creating intuitive interfaces, meaningful interactions, and seamless user journeys.",
          },
          {
            text: "Our product design includes:",
            isBold: true,
            bullets: [
              { text: "Full-cycle product ideation to final handoff" },
              {
                text: "Design systems, scalable components, and atomic UI principles",
              },
              {
                text: "Cross-functional collaboration with development teams",
              },
              {
                text: "Support for MVP launches and iterative design updates",
              },
            ],
          },
          {
            text: "From dashboards to multi-platform apps, we ensure that your product is user-focused, conversion-ready, and easy to use.",
          },
          {
            text: "Keywords: digital product design, MVP UI design, SaaS product design, app UX, scalable UI systems",
          },
        ],
      },
    ],
  },

  "pitch-deck": {
    category: "Design Services",
    title: "Pitch Deck Design",
    subtitle: "Crafting compelling presentations for your business.",
    sections: [
      {
        title: "What We Offer",
        paragraphs: [
          {
            text: "Need to impress investors or stakeholders? We craft compelling, visually engaging pitch decks that blend storytelling, data visualization, and branding, making your ideas stand out and stick.",
          },
          {
            text: "What we deliver:",
            isBold: true,
            bullets: [
              { text: "Branded, presentation-ready slide decks" },
              {
                text: "Custom infographics and icons",
              },
              {
                text: "Strategic structure and copy layout",
              },
              {
                text: "Figma or PowerPoint exports for easy editing",
              },
            ],
          },
          {
            text: "Perfect for startups, fundraising, B2B sales, and demo day presentations.",
          },
          {
            text: "Keywords: pitch deck design, investor deck agency, presentation design, startup pitch, custom slides",
          },
        ],
      },
    ],
  },

  "social-media-posts": {
    category: "Design Services",
    title: "Social Media Posts",
    subtitle: "Creating engaging content for your social media channels.",
    sections: [
      {
        title: "What We Offer",
        paragraphs: [
          {
            text: "Elevate your brand’s online presence with bold, engaging, and scroll-stopping social media creatives. Our designs are optimized for all platforms, ensuring brand consistency, campaign clarity, and higher engagement.",
          },
          {
            text: "Services include:",
            isBold: true,
            bullets: [
              {
                text: "Platform-specific posts (Instagram, LinkedIn, Facebook, X/Twitter)",
              },
              {
                text: "Carousel graphics, reels cover art, and ad banners",
              },
              {
                text: "Seasonal and promotional campaigns",
              },
              {
                text: "Templates you can reuse with your own content",
              },
            ],
          },
          {
            text: "All creatives are designed with your brand tone, color, and audience in mind.",
          },
          {
            text: "Keywords: social media design, Instagram post design, marketing graphics, ad creatives, brand content design.",
          },
        ],
      },
    ],
  },

  // Hero Section Modal
  "hero-cta": {
    category: "Get Started",
    title: "Transform Your Business",
    subtitle: "Let's build something amazing together.",
    sections: [
      {
        title: "Why Choose Us",
        paragraphs: [
          {
            text: "We combine technical expertise with creative problem-solving to deliver solutions that exceed expectations.",
            isBold: true,
            bullets: [
              { text: "10+ Years of Experience" },
              { text: "200+ Successful Projects" },
              { text: "24/7 Support & Maintenance" },
              { text: "Competitive Pricing" },
            ],
          },
        ],
        images: ["/images/truck1.jpg", "/images/truck2.jpg"],
        layout: "text-top",
      },
      {
        title: "Ready to Start?",
        paragraphs: [
          {
            text: "Contact us today to discuss your project requirements.",
            buttons: [
              { text: "Get Free Quote", variant: "primary" },
              { text: "Schedule Call", variant: "secondary" },
            ],
          },
        ],
        layout: "text-only",
      },
    ],
  },
  "org-update": {
    category: "Software Development",
    title: "OrgUpdate",
    subtitle: "Data Visualization & Web Scraping SaaS Platform",
    sections: [
      {
        title: "Project Overview",
        paragraphs: [
          {
            text: "OrgUpdate is a powerful SaaS platform designed to provide real-time job market intelligence by scraping, analyzing, and visualizing job listings, especially from Google Jobs. Built for HR analysts, researchers, and data-driven organizations, it transforms unstructured listings into actionable insights with advanced dashboards and filters.",
            isBold: true,
            bullets: [
              {
                text: "Industry / Client Type: Data Intelligence / HR Tech Startup",
              },
              {
                text: "Project Type: SaaS · Custom Scraper · Real-Time Visualization",
              },
              { text: "Duration: 4+ months (MVP to full launch)" },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB, Puppeteer, Chart.js, Docker, Railway, Vercel",
              },
            ],
          },
        ],
        images: ["/images/case-study/case1.svg"],
        layout: "text-top",
      },
      {
        title: "The Challenge",
        paragraphs: [
          {
            text: "The client’s core challenge was the inability to access consistent, structured data from Google Jobs. Most third-party tools failed repeatedly due to frequent UI changes by Google. These issues caused major product delays, unreliable data feeds, and missed market opportunities. Competitors were also facing similar setbacks, leaving the client at risk of falling behind in a fast-moving HR tech space.",
          },
        ],
        layout: "text-only",
      },
      {
        title: "Our Solution",
        paragraphs: [
          {
            text: "ZYSOFTEC performed a detailed technical audit and designed a custom scraping engine using Puppeteer, independent of Google’s UI structure. The system used adaptive logic, smart DOM mapping, and error handling to ensure ongoing stability. We also built real-time data dashboards and job filtering tools, wrapped in a minimal, responsive interface tailored for HR professionals.",
          },
          {
            text: "Key Features Included:",
            isBold: true,
            bullets: [
              { text: "Resilient Google Jobs scraper engine (UI-agnostic)" },
              { text: "Data enrichment and transformation pipeline" },
              {
                text: "Real-time dashboards with advanced filters (location, keyword, date, type)",
              },
              { text: "Exportable job insights and reports" },
              {
                text: "Agile sprints with weekly feedback, Dockerized CI/CD, and Vercel hosting",
              },
            ],
          },
        ],
        // images: ["/images/truck1.jpg"],
        layout: "text-top",
      },
      {
        title: "The Results",
        paragraphs: [
          {
            text: "The custom scraper achieved 100% uptime despite multiple changes to Google’s UI. The platform launched successfully and consistently fetched real-time job listings, giving the client a clear edge over competitors who remained stalled. Performance improvements—like optimized SSR and fast load speeds—boosted reliability and UX across devices. Key Wins:",
            bullets: [
              { text: "On-time MVP and full launch" },
              { text: "Independence from unreliable tools" },
              {
                text: "~90% increase in scraper stability",
              },
              { text: "Competitive advantage in the HR tech market" },
            ],
          },
        ],
        images: ["/images/case-study/org.png"],
        layout: "text-top",
      },
      {
        title: "Client Testimonial",
        paragraphs: [
          {
            text: "“We were stuck for months trying to fix a scraper issue no one else could solve. ZYSOFTEC’s team not only cracked the code but gave us the edge to finally launch our platform. Their technical knowledge, dedication, and problem-solving attitude were unmatched.”  — Co-Founder, OrgUpdate",
          },
        ],
        layout: "text-only",
      },
    ],
  },
  "simple-numbers": {
    category: "Software Development",
    title: "SimpleNumber",
    subtitle: "Smart Accounting & Financial Analytics Platform",
    sections: [
      {
        title: "Project Overview",
        paragraphs: [
          {
            text: "SimpleNumber is a cloud-based accounting and bookkeeping platform built for professional accountants and financial service firms. It helps manage and analyze clients’ financial records, tax summaries, revenue trends, and cash flows. The platform integrates directly with QuickBooks and transforms complex data into visual insights using custom-built dashboards.",
            isBold: true,
            bullets: [
              {
                text: "Industry / Client Type: Accounting / Financial Services SaaS",
              },
              {
                text: "Project Type: SaaS Platform, QuickBooks Integration, Financial Analytics",
              },
              {
                text: "Duration: Ongoing (Core system completed in ~4 months)",
              },
              {
                text: "Technologies: MERN Stack (MongoDB, Express, React, Node.js), QuickBooks API, Chart.js, JWT, Redis, Docker, Railway, Vercel",
              },
            ],
          },
        ],
        // images: ["/images/truck1.jpg"],
        layout: "text-top",
      },
      {
        title: "The Challenge",
        paragraphs: [
          {
            text: "The client aimed to offer a centralized platform for accountants to access and analyze financial data from multiple client companies via QuickBooks. However, key limitations created roadblocks:",
            isBold: true,
            bullets: [
              {
                text: "QuickBooks API Constraints: Only supported data from one company per session, limiting multi-client aggregation.",
              },
              {
                text: "Login Friction: Required repeated logins through QuickBooks, resulting in poor user experience.",
              },
              {
                text: "Complex Data Structure: Financial data was nested and difficult to transform into actionable reports (e.g., quarterly or year-over-year comparisons).",
              },
              {
                text: "Lack of Analytical Metrics: The API lacked built-in support for trend analysis, comparisons, and data visualizations.",
              },
            ],
          },
        ],
        layout: "text-top",
      },
      {
        title: "Our Solution",
        paragraphs: [
          {
            text: "ZYSOFTEC engineered a powerful middleware and analytics engine to bypass these limitations. A custom aggregation API was developed to fetch data from multiple companies, flatten complex financial structures, and deliver visual insights through a fast and modern MERN dashboard. Key innovations included:",
            bullets: [
              {
                text: "A custom token management system using Redis, enabling persistent 3-month login sessions.",
              },
              {
                text: "Advanced analytics modules showing revenue breakdowns (monthly, quarterly, yearly), comparison reports (Q1 vs Q2, 2023 vs 2024), and industry benchmarks.",
              },
              {
                text: "A simplified data transformation layer that converted hierarchical QuickBooks data into flat, display-ready formats.",
              },
              {
                text: "Filtering by client type, year, revenue range, and more—delivered via a clean, responsive UI optimized for finance professionals.",
              },
            ],
          },
          {
            text: "Development followed an Agile process with weekly demos and iterative client feedback.",
          },
        ],
        // images: ["/images/truck1.jpg"],
        layout: "text-top",
      },
      {
        title: "The Results",
        paragraphs: [
          {
            text: "The project successfully unified multi-company data access—something previously unsupported by QuickBooks alone. The token strategy eliminated 80% of login interruptions, while backend optimizations improved dashboard speed by ~60%. Accountants could now compare financial trends across years and clients seamlessly.",
          },
          {
            text: "Measured impact:",
            isBold: true,
            bullets: [
              { text: "Multiple firm onboarding in the first month" },
              { text: "Login sessions remained active for up to 3 months" },
              {
                text: "Dashboards loaded 30–40% faster post-optimization",
              },
              {
                text: "Dramatic improvement in data visibility and reporting workflows",
              },
            ],
          },
        ],
        // images: ["/images/truck1.jpg"],
        layout: "text-top",
      },
      {
        title: "Client Testimonial",
        paragraphs: [
          {
            text: "“ZYSOFTEC cracked what even QuickBooks support said was impossible. Their ability to build a system that pulls in multiple company' data and analyzes it like magic made all the difference for our product’s success.” — SimpleNumber Product Owner",
          },
        ],
        layout: "text-only",
      },
    ],
  },
  "seller-metrices": {
    category: "Software Development",
    title: "Seller Metrices",
    subtitle: "Advanced Marketplace Analytics & BuyBox Monitoring Platform",
    sections: [
      {
        title: "Project Overview",
        paragraphs: [
          {
            text: "SellerMatrics is a powerful SaaS platform built to serve marketplace sellers—especially those on Takealot.com—by providing real-time insights into inventory, sales performance, and BuyBox tracking. The system transforms minimal third-party API data into deep, actionable analytics through dynamic dashboards, complex financial logic, and cross-platform access via web and mobile.",
            bullets: [
              {
                text: "Industry / Client Type: E-commerce / Marketplace Sellers / SaaS",
              },
              {
                text: "Project Type: SaaS Platform · Marketplace Analytics · Web + Mobile App",
              },
              {
                text: "Duration: 5+ months (Core system, dashboards & mobile apps)",
              },
              {
                text: "Technologies: MERN Stack, React Native, Node.js, MongoDB, JWT, Chart.js, Docker, Vercel, Railway",
              },
            ],
          },
        ],
        images: ["/images/case-study/case2.svg"],
        layout: "text-top",
      },
      {
        title: "The Challenge",
        paragraphs: [
          {
            text: "The client’s core goal was to empower Takealot sellers with detailed sales, profit, and BuyBox analytics. However, the marketplace offered only three limited APIs (sales, inventory, and product), which lacked advanced performance data. This made it extremely challenging to build a comprehensive analytics platform. Additionally, BuyBox ownership was dynamic and difficult to track, depending on multiple variables like pricing, delivery, and seller status. Building a scalable system to process multi-account data, calculate profitability metrics, and support responsive dashboards required creating a complete backend logic layer and API infrastructure from scratch.",
          },
        ],
        layout: "text-top",
      },
      {
        title: "Our Solution",
        paragraphs: [
          {
            text: "ZYSOFTEC focused on extracting maximum value from minimal data by engineering a full custom analytics engine. We built a BuyBox tracking engine that monitored winner shifts and pricing competitiveness, and layered complex financial logic to compute margins, tax, returns, and P&L directly from raw inputs. The platform supported multi-account connections, mobile responsiveness, and advanced filterable dashboards—delivering critical metrics through a seamless user experience.",
            bullets: [
              {
                text: "A custom token management system using Redis, enabling persistent 3-month login sessions.",
              },
              {
                text: "Advanced analytics modules showing revenue breakdowns (monthly, quarterly, yearly), comparison reports (Q1 vs Q2, 2023 vs 2024), and industry benchmarks.",
              },
              {
                text: "A simplified data transformation layer that converted hierarchical QuickBooks data into flat, display-ready formats.",
              },
              {
                text: "Filtering by client type, year, revenue range, and more—delivered via a clean, responsive UI optimized for finance professionals.",
              },
            ],
          },
          {
            text: "Key Highlights:",
            bullets: [
              {
                text: "BuyBox tracker with win/loss analytics and pricing indicators",
              },
              {
                text: "Custom dashboards calculating sales, returns, margins, tax, and profitability",
              },
              {
                text: "Secure multi-seller account support with isolated data processing",
              },
              {
                text: "Real-time visuals with interactive graphs and peak detection",
              },
              {
                text: "Mobile app (React Native) + web dashboard with BI tools",
              },
              {
                text: "Agile methodology with weekly sprints and parallel mobile/web releases",
              },
            ],
          },
        ],
        images: ["/images/case-study/seller_app.png"],
        layout: "text-top",
      },
      {
        title: "The Results",
        paragraphs: [
          {
            text: "The platform enabled sellers to access vital sales and BuyBox data 95% faster than before. With real-time monitoring, they could adjust pricing strategies based on competitive insights. Despite the limited data sources, SellerMatrics delivered a full analytics product that gained rapid user adoption and helped users track daily BuyBox changes, optimize listings, and grow faster.",
          },
          {
            text: "Measured Outcomes:",
            isBold: true,
            bullets: [
              {
                text: "Transformed 3 basic APIs into a full-fledged analytics platform",
              },
              { text: "30% user growth within the first few weeks of launch" },
              {
                text: "Report generation time cut by 70% through advanced filtering",
              },
              {
                text: "Seamless connection and management of multiple Takealot accounts",
              },
            ],
          },
        ],
        // images: ["/images/truck1.jpg"],
        layout: "text-top",
      },
      {
        title: "Client Testimonial",
        paragraphs: [
          {
            text: "“ZYSOFTEC’s team delivered a complete platform from limited data. Their ability to create complex financial logic, handle multi-account scalability, and especially BuyBox monitoring was impressive. Their performance exceeded expectations across every milestone.” — CEO, SellerMatrics",
          },
        ],
        layout: "text-only",
      },
    ],
  },
  personalized: {
    category: "Software Development",
    title: "Personalized: - shirtbay",
    subtitle: "Real-Time Custom Gifting Platform with Live Visual Editing",
    sections: [
      {
        title: "Project Overview",
        paragraphs: [
          {
            text: "Personalized is a fully interactive web platform that allows users to customize gifts—such as t-shirts, mugs, posters, and more—in real time. Users can edit illustrations by changing hairstyles, outfits, text, and other visual elements, all within the browser. Built for the personalized gifting industry, the platform provides a Photoshop-like experience through a clean, mobile-responsive UI without the need for any plugins or software installations.",
            bullets: [
              {
                text: "Industry / Client Type: E-commerce / Creative Gifting / Personalized Products",
              },
              {
                text: "Project Type: SaaS Platform · Real-Time Design Tool · Print-Ready Output Generator",
              },
              {
                text: "Duration: 6 months (From concept to launch-ready platform)",
              },
              {
                text: "Technologies Used: MERN Stack, Fabric.js, Canvas API, TailwindCSS, JWT, Vercel, Railway",
              },
            ],
          },
        ],
        // images: ["/images/truck1.jpg"],
        layout: "text-top",
      },
      {
        title: "The Challenge",
        paragraphs: [
          {
            text: "The client envisioned a browser-based customization engine with the ease of use of a gifting website and the power of design tools like Photoshop. Key challenges included maintaining pixel-perfect alignment during edits, handling live layered rendering for multiple illustration components, and generating print-ready high-resolution files. Additionally, the platform needed to be responsive across all devices while supporting complex user interactions without glitches or distortions.",
          },
        ],
        layout: "text-top",
      },
      {
        title: "Our Solution",
        paragraphs: [
          {
            text: "ZYSOFTEC engineered a real-time customization engine using Fabric.js and HTML5 Canvas, layered within a robust React framework. A modular layer system ensured accurate alignment of elements like hairstyles, props, and backgrounds, with seamless editing capabilities. We also built a high-resolution export engine to generate print-ready PNGs/PDFs and optimized the platform for mobile-first usage to support fast, intuitive editing on any device.",
          },
          {
            text: "Key Deliverables:",
            bullets: [
              {
                text: "Real-time visual editor with customizable templates",
              },
              {
                text: "A layered rendering system for accurate element control",
              },
              {
                text: "High-quality export to support commercial printing",
              },
              {
                text: "Fully responsive design with simplified UX",
              },
              {
                text: "Undo/redo actions and smart alignment for precision",
              },
            ],
          },
          {
            text: "Development followed an agile roadmap with weekly reviews and iterative feature releases.",
          },
        ],
        // images: ["/images/truck1.jpg"],
        layout: "text-top",
      },
      {
        title: "The Results",
        paragraphs: [
          {
            text: "The platform enabled 100% browser-based real-time gift personalization, eliminating the need for any external design tools. Users could preview and edit their designs instantly, while printing vendors received high-resolution, ready-to-print files with consistent quality. The system handled pixel-perfect rendering across devices, improved editing speed (under 200ms interaction delay), and reduced customer support queries due to the intuitive interface.",
          },
          {
            text: "Impact Highlights:",
            isBold: true,
            bullets: [
              {
                text: "Full customization with zero plugins or installs",
              },
              { text: "Increased user satisfaction and conversion rates" },
              {
                text: "Fewer returns due to live previews and visual accuracy",
              },
              {
                text: "Seamless editing across desktop, tablet, and mobile",
              },
            ],
          },
        ],
        // images: ["/images/truck1.jpg"],
        layout: "text-top",
      },
      {
        title: "Client Testimonial",
        paragraphs: [
          {
            text: "“ZYSOFTEC built more than a customization tool—they built an experience. Our users can personalize gifts in real-time and see what they'll get before they buy. That has increased our conversions and reduced returns dramatically.” — CEO, Personalized",
          },
        ],
        layout: "text-only",
      },
    ],
  },
  nujoom: {
    category: "Software Development",
    title: "Nujoom AI",
    subtitle: "Intelligent Voice-Powered Custom Chatbot Builder",
    sections: [
      {
        title: "Project Overview",
        paragraphs: [
          {
            text: "Nujoom AI is an innovative AI SaaS platform that transforms uploaded documents—such as PDFs, invoices, and stories—into intelligent, voice-interactive chatbots. The core of the product lies in its real-time speech-to-speech interaction, enabling users to talk to their AI chatbot and receive spoken answers. With a design inspired by natural human conversations, Nujoom AI offers a new way to interact with document-based information, much like speaking to a smart virtual assistant during a meeting.",
            bullets: [
              {
                text: "IIndustry / Client Type: AI SaaS / Productivity & Voice Tech",
              },
              {
                text: "Project Type: AI Chatbot Builder · Voice AI · File Intelligence Platform",
              },
              {
                text: "Duration: 2.5 Months",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB, OpenAI, Langchain, Azure Cognitive Services",
              },
            ],
          },
        ],
        images: ["/images/case-study/case3.svg"],
        layout: "text-top",
      },
      {
        title: "The Challenge",
        paragraphs: [
          {
            text: "The client envisioned a next-generation chatbot builder that goes beyond text, capable of understanding documents and replying via natural speech. This required real-time parsing of varied file formats, intelligent context-based responses, and seamless voice interaction with customizable voices. Core challenges included integrating speech-to-speech AI for the first time, maintaining low-latency interactions, extracting structured insights from semi-formatted documents, and designing an intuitive voice-first interface.",
          },
        ],
        layout: "text-top",
      },
      {
        title: "Our Solution",
        paragraphs: [
          {
            text: "ZYSOFTEC tackled the project with an AI-first strategy centered around voice interactivity and contextual understanding. We enabled users to upload files (PDF, DOCX, TXT), which were processed through vector embeddings for contextual AI training. Using OpenAI and Langchain, we enabled deep question-answering capabilities. The platform’s voice layer, built on Microsoft Azure’s Speech API, supported multiple male/female voices in different languages. A custom response engine minimized latency and ensured smooth voice transitions. The result was a clean, drag-and-drop chatbot builder with natural voice interaction—accessible via both desktop and mobile.",
          },
          {
            text: "Key Features Delivered:",
            bullets: [
              {
                text: "File-Based AI Chatbot Builder – Instant bot creation from uploaded documents",
              },
              {
                text: 'Contextual Q&A – Smart queries like "What’s the due date?" or "Summarize this PDF"',
              },
              {
                text: "Voice Interaction – Real-time speech-to-speech chat with low-latency responses",
              },
              {
                text: "Custom Voice Selection – Male/female voice options in multiple languages",
              },
              {
                text: "User Dashboard – Manage uploaded files and active bots via a modern, responsive UI",
              },
              {
                text: "Agile Delivery – Weekly demos, Trello-managed tasks, and continuous voice accuracy testing",
              },
            ],
          },
        ],
        // images: ["/images/truck1.jpg"],
        layout: "text-top",
      },
      {
        title: "The Results",
        paragraphs: [
          {
            text: "Nujoom AI launched as a fully functioning voice-powered assistant for document conversations. Users could interact with their content hands-free and receive instant spoken responses. The platform achieved over 90% speech recognition accuracy and dramatically improved document interaction speed, reducing the need for manual reading and customer support.",
          },
          {
            text: "Impact Highlights:",
            isBold: true,
            bullets: [
              {
                text: "60% faster document comprehension via voice queries",
              },
              { text: "90%+ speech accuracy across multiple file formats" },
              {
                text: "High engagement through human-like AI interaction",
              },
              {
                text: "Multilingual support expanded global accessibility",
              },
              {
                text: "Fully self-serve platform with intuitive design and usability",
              },
            ],
          },
        ],
        images: ["/images/case-study/nujoom-zysoftec.png"],
        layout: "text-top",
      },
      {
        title: "Client Testimonial",
        paragraphs: [
          {
            text: "“Nujoom AI is now more than just a chatbot – it's a conversational assistant for files. ZYSOFTEC delivered this complex solution with clarity, creativity, and solid execution.” — Product Owner, Nujoom AI",
          },
        ],
        layout: "text-only",
      },
    ],
  },
  cherrysprites: {
    category: "Software Development",
    title: "Cherrysprites",
    subtitle: "Social Habit Tracking & Timezone-Smart Challenge App",
    sections: [
      {
        title: "Project Overview",
        paragraphs: [
          {
            text: "Cherrysprites is a global, socially driven habit tracking app designed to help users build consistent habits while staying connected with friends across the world. With intelligent timezone handling, a collaborative challenge system, and a calendar-based UI, the app keeps users motivated and accountable, regardless of their location.",
            bullets: [
              {
                text: "Industry / Client Type: Health & Wellness / Lifestyle / Productivity",
              },
              {
                text: "Duration: 3–4 months (Concept to MVP Launch)",
              },
              {
                text: "Technologies Used: MERN Stack (MongoDB, Express.js, React.js, Node.js), Moment-Timezone, FullCalendar, JWT Auth, Socket.io, Vercel, Railway",
              },
            ],
          },
        ],
        // images: ["/images/truck1.jpg"],
        layout: "text-top",
      },
      {
        title: "The Challenge",
        paragraphs: [
          {
            text: "The client envisioned a habit tracker that worked seamlessly across different time zones while enabling group challenges. One major challenge was ensuring that users in various parts of the world saw the same habit schedule adjusted locally without losing synchronization. Other core requirements included an invite-based challenge model, a real-time sync engine for shared progress, and a visually appealing calendar to manage daily goals. Solving timezone conflicts while maintaining accurate data flow across global users was critical.",
          },
        ],
        layout: "text-only",
      },
      {
        title: "Our Solution",
        paragraphs: [
          {
            text: "ZYSOFTEC developed a full MERN-based solution with a focus on timezone-aware logic, collaborative tracking, and a user-friendly calendar UI. Using Moment-Timezone and FullCalendar, we enabled localized time displays while syncing challenge logic globally. Users could create and join habits, view progress in real-time, and track completions through a responsive monthly calendar. Social feeds encouraged participation and accountability, while a clean React + Tailwind UI ensured smooth usability across devices.",
          },
        ],
        layout: "text-only",
      },
      {
        title: "The Results",
        paragraphs: [
          {
            text: "Cherrysprites successfully bridged the timezone gap, enabling users to engage in group habits globally without confusion. The interactive calendar made habit management intuitive, while the social layer boosted consistency and daily check-ins. Our architecture delivered high accuracy in timezone handling and significantly increased user engagement through collaborative features.",
          },
        ],
        layout: "text-only",
      },
      {
        title: "Client Testimonial",
        paragraphs: [
          {
            text: "“ZYSOFTEC solved a real challenge — making timezone-based habit tracking truly global and social. Cherrysprites became a unique experience thanks to their smart architecture and intuitive UX.” — Founder, Cherrysprites",
          },
        ],
        layout: "text-only",
      },
    ],
  },
  "ai-solutions-advisor": {
    category: "Software Development",
    title: "SaaS (Software as a Service) Development",
    sections: [
      {
        paragraphs: [
          {
            text: "At ZYSOFTEC, we specialize in designing and developing powerful, scalable, and secure SaaS platforms that support recurring revenue and long-term growth. Whether you're building a product from scratch or scaling an existing solution, our full-stack development teams ensure seamless performance across user onboarding, subscription billing, real-time dashboards, access control, and analytics.",
          },

          // {
          //   text: "Whether you’re launching a niche MVP or a complex B2B platform, ZYSOFTEC builds SaaS that scale.s",
          // },
          {
            text: "We have delivered robust SaaS platforms across various industries:",
          },
        ],
        layout: "text-only",
      },
      {
        title: "1. Neumandate – Tax Consultant Automated Software",
        paragraphs: [
          {
            text: "Neumandate is a comprehensive system designed to streamline and modernize the tax consulting workflow. From generating custom offers to handling secure payments and client onboarding, it provides an end‑to‑end solution that reduces manual effort and accelerates service delivery. Advanced configuration options, automated PDF generation, and integrated signature/print capabilities ensure both consultants and clients enjoy a seamless, professional experience.",
            bullets: [
              {
                text: "Dynamic offer-creation module, Secure offer sending",
              },
              {
                text: "Stripe Payment Integration, Downgrade, upgrade, trial extension, cancel subscription",
              },
              {
                text: "Client Onboarding Workflow, PDF Generation & E-Signatures",
              },
              {
                text: "Technologies & Tools Used: Next.js, Node.js, MongoDB, Azure Services, PDF libraries, Email services",
              },
            ],
          },
        ],
        images: ["/images/case-study/neu.png"],
        layout: "text-top",
      },
      {
        title: "2. Seller Mertics – Seller Marketplace Takealot Platform",
        paragraphs: [
          {
            text: "SellerMetrics empowers online retailers with real‑time visibility into their marketplace performance across both web and mobile. It aggregates critical metrics—like Buy Box share and financial KPIs—into an intuitive dashboard, unlocking data‑driven decision‑making. With interactive charts and customizable reports, sellers can pinpoint trends, optimize pricing strategies, and maximize profitability with confidence.",
            bullets: [
              {
                text: "3rd party API Integration, complex queries and data handling",
              },
              {
                text: "Stripe Integration, Local payment gateway Integration",
              },
              {
                text: "Graphs, Charts Libraries and admin features",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB, Takealot API's, React Native",
              },
            ],
          },
        ],
        images: ["/images/case-study/case2.svg"],
        layout: "text-top",
      },
      {
        title: "3. Fiksuu – Business Card Panel ",
        paragraphs: [
          {
            text: "Fiksuu centralizes digital business card management. It supports three user tiers—admin, standard user, and business user—each with granular permissions, and offers four distinct digital profile templates to suit different engagement scenarios. Deep Zapier integration automates workflows and lead capture, while real‑time analytics and count metrics empower teams to monitor performance.",
            bullets: [
              {
                text: "Role‑Based Access Control & Multi‑Profile Management",
              },
              {
                text: "QR code scanning, Social media sharing",
              },
              {
                text: "Zapier Integration, Lead Capture & Page views and Analytics",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB",
              },
            ],
          },
        ],
        images: ["/images/case-study/fiksu.png"],
        layout: "text-top",
      },
      {
        title:
          "4. Soul Match – Relationship Matching & Behavioral Logic Platform",
        paragraphs: [
          {
            text: "SoulMatch is an intuitive matchmaking application designed to foster meaningful connections through data‑driven compatibility and behavioral insights. It guides users through a seamless registration and profile setup, then leverages advanced partner‑preference filters and proprietary logic to suggest optimal matches. Integrated live chat and request workflows ensure conversations spark naturally, while robust privacy controls keep user data secure.",
            bullets: [
              {
                text: "User Registration & Onboarding",
              },
              {
                text: "Partner Preference Engine",
              },
              {
                text: "Live Chat & Messaging, Match Requests & Notification",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB, Firebase, Sockets",
              },
            ],
          },
        ],
        images: ["/images/case-study/soul.png"],
        layout: "text-top",
      },
      {
        title: "5. Undiscovered – Startup Discovery & Deal Flow Platform",
        paragraphs: [
          {
            text: "Undiscovered is a centralized hub for venture capitalists, angel investors, and corporate scouts to source, evaluate, and manage promising early‑stage startups. It combines intelligent discovery algorithms with a structured deal‑flow pipeline to streamline investment research and decision-making. ",
            bullets: [
              {
                text: "Smart Startup Sourcing, Analytics & Reporting",
              },
              {
                text: "Duration: 2.5 Months",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB, ",
              },
            ],
          },
        ],
        images: ["/images/case-study/undisc.png"],
        layout: "text-top",
      },
      {
        title: "6. AI Fornia – AI‑Driven Productivity Assistant",
        paragraphs: [
          {
            text: "Aifornia is a smart platform designed to centralize and accelerate your daily workflow using AI. Users can submit and discover cutting‑edge AI tools, receive curated newsletters, and automate routine tasks—all through a unified interface. By combining intelligent recommendations with seamless integrations, Aifornia helps individuals and teams stay organized, informed, and efficient.",
            bullets: [
              {
                text: "AI Tools Library, Newsletters",
              },
              {
                text: "Stripe Integration",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB",
              },
            ],
          },
        ],
        images: ["/images/case-study/aifornia.png"],
        layout: "text-top",
      },
      {
        title: "7. Revlyx – Volunteer & Community Engagement Platform",
        paragraphs: [
          {
            text: "Revlyx is a SaaS solution that empowers nonprofits by connecting them with individual volunteers and corporate partners to drive social impact. Volunteers can discover, filter, and sign up for opportunities that match their interests and schedules, while organizations manage events, donations, and outreach from a unified dashboard. The platform’s intuitive community‑management tools foster collaboration, streamline coordination, and help track engagement metrics for continuous improvement. ",
            bullets: [
              {
                text: "Cross‑Sector Connection, Opportunity Discovery",
              },
              {
                text: "Role‑Based Dashboards",
              },
              {
                text: "Engagement Analytics",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB",
              },
            ],
          },
        ],
        images: ["/images/case-study/revlyx.png"],
        layout: "text-top",
      },
    ],
  },
  "data-science": {
    category: "Software Development",
    title: "Artificial Intelligence (AI & ML Solutions)",
    sections: [
      {
        paragraphs: [
          {
            text: "At ZYSOFTEC, we integrate Artificial Intelligence (AI) and Machine Learning (ML) to help businesses enhance user personalization, automate operations, and make predictive, data-driven decisions.",
          },
          {
            text: "Our AI capabilities include:",
            bullets: [
              {
                text: "Machine Learning & Predictive Analytics",
              },
              {
                text: "Natural Language Processing (NLP)",
              },
              {
                text: "Chatbots & Virtual Assistants",
              },
              {
                text: "Image, Voice & Data Recognition",
              },
              {
                text: "Custom AI Algorithm Development",
              },
            ],
          },
          {
            text: "Featured AI-driven projects:",
            // bullets: [
            //   {
            //     text: "Nujoom – AI-based astrology and prediction engine",
            //   },
            //   {
            //     text: "SuitedStories – Fashion AI assistant based on user behavior",
            //   },
            //   {
            //     text: "Aifornia – Smart productivity with real-time AI task suggestions",
            //   },
            // ],
          },
          // {
          //   text: "Our AI solutions are built to learn, evolve, and optimize continuously, adding long-term value to your digital products.",
          // },
        ],
        layout: "text-only",
      },
      {
        title: "1. Suited Stories – AI‑Powered Personalized Storybook Creator",
        paragraphs: [
          {
            text: "Suited Stories transforms simple user prompts into beautifully illustrated, fully narrated storybooks. Leveraging the OpenAI library for dynamic content generation, it offers a seamless end‑to‑end experience—from prompt input and AI‑driven writing to e‑commerce and multimedia reading. Integrated payment, PDF formatting, and voice‑over features make Suited Stories both a creator’s playground and a reader’s delight.",
            bullets: [
              {
                text: "Prompt‑Driven Story Generation, E‑Commerce & Stripe Integration",
              },
              {
                text: "Custom PDF Formatting, Downloadable & Printable Output",
              },
              {
                text: "Voice‑Over & Interactive Reader",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB, OpenAI",
              },
            ],
          },
        ],
        images: ["/images/case-study/suited.png"],
        layout: "text-top",
      },
      {
        title: "2. Nujoom AI – Seller Marketplace Takealot Platform",
        paragraphs: [
          {
            text: "Nujoom AI is an innovative AI SaaS platform that transforms uploaded documents—such as PDFs, invoices, and stories—into intelligent, voice-interactive chatbots. The core of the product lies in its real-time speech-to-speech interaction, enabling users to talk to their AI chatbot and receive spoken answers. With a design inspired by natural human conversations, Nujoom AI offers a new way to interact with document-based information, much like speaking to a smart virtual assistant during a meeting.",
            bullets: [
              {
                text: "IIndustry / Client Type: AI SaaS / Productivity & Voice Tech",
              },
              {
                text: "Project Type: AI Chatbot Builder · Voice AI · File Intelligence Platform",
              },
              {
                text: "Duration: 2.5 Months",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB, OpenAI, Langchain, Azure Cognitive Services",
              },
            ],
          },
        ],
        images: ["/images/case-study/case3.svg"],
        layout: "text-top",
      },
      {
        title: "3. AI Fornia – AI‑Driven Productivity Assistant",
        paragraphs: [
          {
            text: "Aifornia is a smart platform designed to centralize and accelerate your daily workflow using AI. Users can submit and discover cutting‑edge AI tools, receive curated newsletters, and automate routine tasks—all through a unified interface. By combining intelligent recommendations with seamless integrations, Aifornia helps individuals and teams stay organized, informed, and efficient.",
            bullets: [
              {
                text: "AI Tools Library, Newsletters",
              },
              {
                text: "Stripe Integration",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB",
              },
            ],
          },
        ],
        images: ["/images/case-study/aifornia.png"],
        layout: "text-top",
      },
    ],
  },
  "data-engineering": {
    category: "Software Development",
    title: "E-Commerce Development",
    sections: [
      {
        paragraphs: [
          {
            text: "Build a converting, scalable e-commerce platform with ZYSOFTEC. We develop fully customized e-commerce solutions that blend beautiful UX with robust backend features like payment integration, product management, secure checkout, and performance optimization.",
          },
          {
            text: "Our e-commerce services include:",
            bullets: [
              {
                text: "Single-brand stores & marketplaces",
              },
              {
                text: "Inventory & order management",
              },
              {
                text: "Mobile-optimized UI/UX",
              },
              {
                text: "Integrated shipping, payments, and analytics",
              },
              {
                text: "Multilingual & multi-currency setups",
              },
            ],
          },
          {
            text: "Successful e-commerce projects:",
            bullets: [
              {
                text: "Denka – Fashion and lifestyle retail store",
              },
              {
                text: "NB – Consumer products brand with custom backend",
              },
              {
                text: "Les Veggies – Organic food and health products",
              },
              {
                text: "Clubfoot – Customized footwear and medical shoes",
              },
            ],
          },
          {
            text: "We create stores that are not just beautiful — but built to sell.",
          },
        ],
        layout: "text-only",
      },
      {
        title: "1. Denka – Seller Marketplace Takealot Platform",
        paragraphs: [
          {
            text: "Denka is a scalable, full‑featured e‑commerce solution designed to help brands launch and grow their online stores. It combines a sleek, responsive storefront with a powerful admin dashboard for product, order, and customer management. Secure payment flows and marketing integrations ensure a seamless shopping experience and drive conversions. Built‑in analytics and automation tools empower merchants to optimize performance and scale effortlessly.",
            bullets: [
              {
                text: "Dynamic Product Catalog, Secure Checkout & Payments",
              },
              {
                text: "Analytics & Marketing Automation",
              },
              {
                text: "Duration: 3 Months",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB, Express.js",
              },
            ],
          },
        ],
        images: ["/images/case-study/denka_web.png"],
        layout: "text-top",
      },
      {
        title: "2. Noor Boutique – Seller Marketplace Takealot Platform",
        paragraphs: [
          {
            text: "Noor Boutique is a custom-built online storefront tailored for selling hijabs, scarves, and related fashion accessories. Developed as part of the Noor Stores ecosystem (noorstores.ly), it delivers a seamless shopping experience with full Arabic/English support, responsive design, and real‑time inventory sync across channels. Integrated POS, delivery management, and analytics tools empower both online and brick‑and‑mortar operations to run in harmony.",
            bullets: [
              {
                text: "Bilingual UI & RTL Support",
              },
              {
                text: "Integrated POS & Delivery, Loyality, Offers, Live Chat",
              },
              {
                text: "Analytics & Reporting, CMS",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB",
              },
            ],
          },
        ],
        images: ["/images/case-study/nb-web.png"],
        layout: "text-top",
      },
      {
        title: "3. Les Veggies – Seller Marketplace Takealot Platform",
        paragraphs: [
          {
            text: "Les Veggies is a cross‑platform mobile and web solution built for Canadian customers to discover, select, and receive farm‑fresh fruits and vegetables at their doorstep. With an intuitive UI and real‑time inventory updates, users can explore seasonal offerings, set up recurring orders, and track deliveries seamlessly. Backed by a robust Node.js and PostgreSQL backend, the platform ensures secure payments, efficient logistics, and a delightful shopping experience from browse to bite.",
            bullets: [
              {
                text: "Cross‑Platform & Admin Dashboard",
              },
              {
                text: "Real‑Time Tracking & Notifications",
              },
              {
                text: "Dynamic Product Catalog",
              },
              {
                text: "Technologies Used: Next.js, Node.js, Firebase",
              },
            ],
          },
        ],
        images: ["/images/case-study/les_veggies_panel.png"],
        layout: "text-top",
      },
      {
        title: "4. Club Foot – Seller Marketplace Takealot Platform",
        paragraphs: [
          {
            text: "Club Foot is a purpose‑built platform connecting families of children with disabilities to buy and sell gently used and new adaptive equipment. Featuring an inclusive, accessible interface and community‑driven support, it streamlines listing creation, secure transactions, and delivery logistics so caregivers can focus on finding the right tools for their child’s needs. With robust safety features and tailored search filters, Club Foot fosters trust and ease of use in every interaction.",
            bullets: [
              {
                text: "IIndustry / Client Type: SaaS / Marketplace",
              },
              {
                text: "Project Type: Disbaled kidsboot selling and buying marketplace",
              },
              {
                text: "Duration: 2.5 Months",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB, shipping 3rd party api",
              },
            ],
          },
        ],
        images: ["/images/case-study/club_foot.png"],
        layout: "text-top",
      },
    ],
  },
  mlops: {
    category: "Software Development",
    title: "CRM & ERP Development",
    sections: [
      {
        paragraphs: [
          {
            text: "Streamline your business processes with custom-built CRM and ERP systems by ZYSOFTEC. We build intelligent, modular platforms that centralize data, automate workflows, and offer real-time insights across departments like sales, HR, finance, inventory, and operations.",
          },
          {
            text: "What we offer:",
            bullets: [
              {
                text: "Custom CRM for sales and customer tracking",
              },
              {
                text: "ERP systems for HR, inventory, and production",
              },
              {
                text: "Integrations with QuickBooks, Stripe, and 3rd-party APIs",
              },
              {
                text: "Real-time dashboards and role-based access control",
              },
            ],
          },
          {
            text: "CRM/ERP implementations:",
            bullets: [
              {
                text: "GMS – Garments Management System (production & HR automation)",
              },
              {
                text: "Denka ERP – Retail inventory and POS system",
              },
              {
                text: "One Union – Membership and records automation",
              },
              {
                text: "Shedrrip – Real estate operations management",
              },
              {
                text: "QuickBooks Suite – Accounting integration and automation tools",
              },
            ],
          },
          {
            text: "With ZYSOFTEC CRM/ERP, you don’t adapt to the system—the system adapts to you.",
          },
        ],
        layout: "text-only",
      },
     
      {
        title: "1. Denka CRM – Sales & E-commerce CRM",
        paragraphs: [
          {
            text: "Denka CRM extends the Denka ecosystem by providing a unified platform to manage customer interactions, sales pipelines, and post‑purchase support. Seamlessly integrated with Denka’s e‑commerce and order data, it empowers teams to nurture leads, track opportunities, and deliver personalized experiences at scale. With automation workflows, omnichannel communication, and real‑time analytics, Denka CRM helps businesses deepen customer relationships and drive repeat revenue.            ",
            bullets: [
              {
                text: "Contact & Lead Management, Visual Sales Pipeline",
              },
              {
                text: "Workflow Automation, Live chat",
              },
              {
                text: "Duration: 2 Months",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB",
              },
            ],
          },
        ],
        images: ["/images/case-study/denka_crm.png"],
        layout: "text-top",
      },
      {
        title: "2. One Union – Seller Marketplace Takealot Platform",
        paragraphs: [
          {
            text: "One Union is a specialized CRM platform tailored for businesses handling goods shipment and logistics. It centralizes customer inquiries, quote management, and order processing into a single interface, while providing real‑time tracking and cost estimation tools. With granular, role‑based access controls and integrated communication channels, teams can collaborate efficiently and deliver exceptional service from quote to delivery.",
            bullets: [
              {
                text: "Customer & Quote Management",
              },
              {
                text: "Shipment Tracking Dashboard",
              },
              {
                text: "Duration: 2.5 Months",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB",
              },
            ],
          },
        ],
        images: ["/images/case-study/one_union.png"],
        layout: "text-top",
      },
      {
        title: "3. Sheddrip – Seller Marketplace Takealot Platform",
        paragraphs: [
          {
            text: "Shedrrip is a comprehensive platform designed to help real estate professionals centralize and manage their property portfolios. From detailed asset records to maintenance workflows and financial reporting, it offers end‑to‑end visibility into every building under management. Role‑based access ensures teams—from site managers to executives—see exactly the data and tools they need, while map integrations and automated alerts keep everyone aligned and proactive.            ",
            bullets: [
              {
                text: "Property Catalog & Metadata",
              },
              {
                text: "Analytics & Reporting",
              },
              {
                text: "Duration: 2 Months",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB",
              },
            ],
          },
        ],
        images: ["/images/case-study/sheddrip.png"],
        layout: "text-top",
      },
      {
        title:
          "4. Goods Management System ",
        paragraphs: [
          {
            text: "The Goods Management System centralizes every stage of the supply chain—from procurement and receiving to storage, order fulfillment, and dispatch. With real‑time stock visibility and automated workflows, it minimizes stockouts and overstocking while boosting operational efficiency. Role‑based access ensures warehouse staff, procurement teams, and managers each see the tools and data they need without clutter.",
            bullets: [
              {
                text: "weekly & Monthly German calendar - Stock Tracking",
              },
              {
                text: "Role‑Based Access Control",
              },
              {
                text: "Duration: 1 Month",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB",
              },
            ],
          },
        ],
        images: ["/images/case-study/gms.png"],
        layout: "text-top",
      },
      {
        title: "5. Simple Numbers ",
        paragraphs: [
          {
            text: "SimpleNumber is a cloud-based accounting and bookkeeping platform built for professional accountants and financial service firms. It helps manage and analyze clients’ financial records, tax summaries, revenue trends, and cash flows. The platform integrates directly with QuickBooks and transforms complex data into visual insights using custom-built dashboards.            ",
            bullets: [
              {
                text: "Industry / Client Type: Accounting / Financial Services SaaS",
              },
              {
                text: "Project Type: SaaS Platform, QuickBooks Integration",
              },
              {
                text: "Financial Analytics",
              },
              {
                text: "Technologies Used:  MERN Stack (MongoDB, Express, React, Node.js), QuickBooks API, Chart.js, JWT, Redis, Docker, Railway, Vercel",
              },
            ],
          },
        ],
        // images: ["/images/case-study/aifornia.png"],
        layout: "text-top",
      },
    ],
  },
  "artificial-intelligence": {
    category: "Software Development",
    title: "Mobile App Development",
    sections: [
      {
        paragraphs: [
          {
            text: "From idea to App Store, ZYSOFTEC creates powerful, intuitive mobile applications for both Android and iOS platforms. Using native and cross-platform frameworks, we build apps that ensure smooth functionality, beautiful interfaces, and long-term scalability.",
          },
          {
            text: "Mobile app services:",
            bullets: [
              {
                text: "iOS and Android Development",
              },
              {
                text: "React Native, Flutter",
              },
              {
                text: "In-app purchases, chat, GPS, camera, & notifications",
              },
              {
                text: "Performance and battery optimization",
              },
              {
                text: "App Store publishing & support",
              },
            ],
          },
          {
            text: "Mobile apps we’ve built:",
            bullets: [
              {
                text: "Les Veggies App – Grocery and product ordering",
              },
              {
                text: "SoulMatch – Relationship discovery app",
              },
              {
                text: "Txtract – Document and data extraction",
              },
              {
                text: "Eventbright – 3 integrated APIs",
              },
              {
                text: "Finance App, Job Portal, Healthcare App – Industry-specific mobile solutions",
              },
            ],
          },
          {
            text: "Let us help you bring your app idea to life with innovation and precision.",
          },
        ],
        layout: "text-only",
      },
      {
        title: "1. TxTract – Data Extration App",
        paragraphs: [
          {
            text: "Txtract automates the conversion of unstructured documents into clean, structured data. With support for PDFs, scanned images, spreadsheets, and web pages, it leverages OCR and NLP to identify tables, key‑value pairs, and free‑form text. Users can ingest files via drag‑and‑drop or integrations, then export results in CSV, JSON, or directly into databases and downstream systems—streamlining reporting, analysis, and automation pipelines.            ",
            bullets: [
              {
                text: "Multi‑format Ingestion: Upload or connect to PDFs, images (JPG/PNG), Excel/CSV, and web URLs",
              },
              {
                text: "OCR & NLP‑Driven Parsing",
              },
              {
                text: "Flexible Export Options",
              },
              {
                text: "Technologies Used: React Native, Node.js, MongoDB, Azure Services",
              },
            ],
          },
        ],
        images: ["/images/case-study/txtract.png"],
        layout: "text-top",
      },
      {
        title: "2. EventBright – Events Order Platform",
        paragraphs: [
          {
            text: "EventBright brings together events, restaurants, and shops from Ticketmaster, Yelp, and Eventbrite into one seamless experience. Users can explore what's happening nearby—whether it’s a concert, a pop‑up market, or a new café—without switching between multiple apps. Intuitive filters, map views, and personalized recommendations help people find and plan outings that match their interests and schedules.            ",
            bullets: [
              {
                text: "Multi‑Source API Integrations",
              },
              {
                text: "Search and Filters, Personalized Recommendations",
              },
              {
                text: "Duration: 30 Days",
              },
              {
                text: "Technologies Used: React Native, Node.js, MongoDB, 3rd Party APIs",
              },
            ],
          },
        ],
        // images: ["/images/case-study/aifornia.png"],
        layout: "text-top",
      },
      {
        title: "3. Seller Metrics – Seller Marketplace Takealot Platform",
        paragraphs: [
          {
            text: "SellerMetrics empowers online retailers with real‑time visibility into their marketplace performance across both web and mobile. It aggregates critical metrics—like Buy Box share and financial KPIs—into an intuitive dashboard, unlocking data‑driven decision‑making. With interactive charts and customizable reports, sellers can pinpoint trends, optimize pricing strategies, and maximize profitability with confidence.            ",
            bullets: [
              {
                text: "Financial Dashboard",
              },
              {
                text: "Advanced Graphs & Charts",
              },
              {
                text: "Cross‑Platform Access",
              },
              {
                text: "Technologies Used: React Native, Node.js, MongoDB, 3rd Party API",
              },
            ],
          },
        ],
        images: ["/images/case-study/seller_app.png"],
        layout: "text-top",
      },
      {
        title: "4. SoulMatch – Seller Marketplace Takealot Platform",
        paragraphs: [
          {
            text: "SoulMatch is an intuitive matchmaking application designed to foster meaningful connections through data‑driven compatibility and behavioral insights. It guides users through a seamless registration and profile setup, then leverages advanced partner‑preference filters and proprietary logic to suggest optimal matches. Integrated live chat and request workflows ensure conversations spark naturally, while robust privacy controls keep user data secure.            ",
            bullets: [
              {
                text: "Profile Setup & Customization",
              },
              {
                text: "Partner Preference Engine",
              },
              {
                text: "Live Chat & Messaging",
              },
              {
                text: "Technologies Used: React Native, Next.js, Node.js, MongoDB",
              },
            ],
          },
        ],
        images: ["/images/case-study/soulmatch_app.png"],
        layout: "text-top",
      },
      {
        title: "5. Les Veggies – E-commerce",
        paragraphs: [
          {
            text: "Les Veggies is a cross‑platform mobile and web solution built for Canadian customers to discover, select, and receive farm‑fresh fruits and vegetables at their doorstep. With an intuitive UI and real‑time inventory updates, users can explore seasonal offerings, set up recurring orders, and track deliveries seamlessly. Backed by a robust Node.js and Firebase backend, the platform ensures secure payments, efficient logistics, and a delightful shopping experience from browse to bite.            ",
            bullets: [
              {
                text: "Dynamic Product Catalog",
              },
              {
                text: "Cross‑Platform & Admin Dashboard",
              },
              {
                text: "Real‑Time Tracking & Notifications",
              },
              {
                text: "Technologies Used: React Native, Next.js, Node.js, Firebase",
              },
            ],
          },
        ],
        images: ["/images/case-study/les_veggies_app.png"],
        layout: "text-top",
      },
    ],
  },
  "intelligent-automation": {
    category: "Software Development",
    title: "Multi-Vendor Marketplace Development",
    sections: [
      {
        paragraphs: [
          {
            text: "Launch your multi-vendor platform with complete control and scalability. ZYSOFTEC builds marketplace solutions that support seamless vendor onboarding, commission management, order tracking, and payment processing—all through a single admin dashboard.",
          },
          {
            text: "Marketplace features:",
            bullets: [
              {
                text: "Vendor dashboards & commission tracking",
              },
              {
                text: "Order and product management",
              },
              {
                text: "Secure payment integration",
              },
              {
                text: "Review systems & support chat",
              },
              {
                text: "Custom delivery zones and tax setup",
              },
            ],
          },
          {
            text: "Success stories:",
            bullets: [
              {
                text: "Agent Listing Service",
              },
              {
                text: "Carfig – Auto services and spare parts marketplace",
              },
            ],
          },
          {
            text: "We build marketplaces that connect people, products, and profits.",
          },
        ],
        layout: "text-only",
      },
      {
        title:
          "1. Agent Listing Service – Agent to Agent Referral",
        paragraphs: [
          {
            text: "ALS is a dual-portal web application that connects real-estate agents and consumers through seamless property listings and referral workflows. Consumers can browse detailed property sites, while agents manage listings, create branded microsites, and collaborate via live chat and community boards. Built-in referral tracking and group features empower agents to share leads and grow networks efficiently.            ",
            bullets: [
              {
                text: "Property Listing & Branded Microsites",
              },
              {
                text: "Real-Time Collaboration, Ad posting, Digital Business card",
              },
              {
                text: "Agent-to-Agent Referral Engine, Interactive Map Search & Profiles",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB",
              },
            ],
          },
        ],
        // images: ["/images/case-study/aifornia.png"],
        layout: "text-top",
      },
      {
        title: "2. Carfig – Vehicle Listing platform",
        paragraphs: [
          {
            text: "CarFig is a full‑featured car listing and dealership management solution that brings together real‑time market intelligence, seamless browsing, and powerful inventory controls. By integrating MarketCheck APIs, it offers up‑to‑date pricing, valuation, and market trends alongside customizable dealer panels. CarFig’s intuitive interface and advanced filters help buyers find the perfect vehicle while empowering dealers to track, update, and promote their inventory efficiently.            ",
            bullets: [
              {
                text: "MarketCheck API Integration:",
              },
              {
                text: "Advanced Search & Filters",
              },
              {
                text: "Dealer Inventory Panels",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB, Marketcheck API Integration",
              },
            ],
          },
        ],
        images: ["/images/case-study/carfig.png"],
        layout: "text-top",
      },
      // {
      //   title: "3. System Like Fresha – Seller Marketplace Takealot Platform",
      //   paragraphs: [
      //     {
      //       text: "Nujoom AI is an innovative AI SaaS platform that transforms uploaded documents—such as PDFs, invoices, and stories—into intelligent, voice-interactive chatbots. The core of the product lies in its real-time speech-to-speech interaction, enabling users to talk to their AI chatbot and receive spoken answers. With a design inspired by natural human conversations, Nujoom AI offers a new way to interact with document-based information, much like speaking to a smart virtual assistant during a meeting.",
      //       bullets: [
      //         {
      //           text: "IIndustry / Client Type: AI SaaS / Productivity & Voice Tech",
      //         },
      //         {
      //           text: "Project Type: AI Chatbot Builder · Voice AI · File Intelligence Platform",
      //         },
      //         {
      //           text: "Duration: 2.5 Months",
      //         },
      //         {
      //           text: "Technologies Used: Next.js, Node.js, MongoDB, OpenAI, Langchain, Azure Cognitive Services",
      //         },
      //       ],
      //     },
      //   ],
      //   images: ["/images/case-study/aifornia.png"],
      //   layout: "text-top",
      // },
    ],
  },
  "data-engineering-description": {
    category: "Software Development",
    title: "Landing Page Design & Development",
    sections: [
      {
        paragraphs: [
          {
            text: "ZYSOFTEC creates high-converting, SEO-friendly landing pages for brands, campaigns, and products. Our designs are clean, mobile-optimized, and crafted to maximize user engagement and lead generation.",
          },
          {
            text: "Landing page features:",
            bullets: [
              {
                text: "Fast loading speed (Core Web Vitals optimized)",
              },
              {
                text: "SEO meta-data, Open Graph, and schema markup",
              },
              {
                text: "Mobile-first, responsive design",
              },
              {
                text: "Conversion tracking and integrations",
              },
              {
                text: "Built with React, Next.js, or custom stacks",
              },
            ],
          },
          {
            text: "Campaigns delivered:",
            bullets: [
              {
                text: "Inclusive – Diversity initiative campaign page",
              },
              {
                text: "Project Wildling – Eco awareness & community drive",
              },
              {
                text: "Dental Kramer – Lead-gen landing for dental clinics",
              },
              {
                text: "RentUpay – Payment and rental landing experience",
              },
            ],
          },
          {
            text: "We design pages that convert clicks into customers.",
          },
        ],
        layout: "text-only",
      },
      {
        title: "1. Inclusive – Seller Marketplace Takealot Platform",
        paragraphs: [
          {
            text: "Nujoom AI is an innovative AI SaaS platform that transforms uploaded documents—such as PDFs, invoices, and stories—into intelligent, voice-interactive chatbots. The core of the product lies in its real-time speech-to-speech interaction, enabling users to talk to their AI chatbot and receive spoken answers. With a design inspired by natural human conversations, Nujoom AI offers a new way to interact with document-based information, much like speaking to a smart virtual assistant during a meeting.",
            bullets: [
              {
                text: "IIndustry / Client Type: AI SaaS / Productivity & Voice Tech",
              },
              {
                text: "Project Type: AI Chatbot Builder · Voice AI · File Intelligence Platform",
              },
              {
                text: "Duration: 2.5 Months",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB, OpenAI, Langchain, Azure Cognitive Services",
              },
            ],
          },
        ],
        images: ["/images/case-study/aifornia.png"],
        layout: "text-top",
      },
      {
        title: "2. Revlyx – Seller Marketplace Takealot Platform",
        paragraphs: [
          {
            text: "Nujoom AI is an innovative AI SaaS platform that transforms uploaded documents—such as PDFs, invoices, and stories—into intelligent, voice-interactive chatbots. The core of the product lies in its real-time speech-to-speech interaction, enabling users to talk to their AI chatbot and receive spoken answers. With a design inspired by natural human conversations, Nujoom AI offers a new way to interact with document-based information, much like speaking to a smart virtual assistant during a meeting.",
            bullets: [
              {
                text: "IIndustry / Client Type: AI SaaS / Productivity & Voice Tech",
              },
              {
                text: "Project Type: AI Chatbot Builder · Voice AI · File Intelligence Platform",
              },
              {
                text: "Duration: 2.5 Months",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB, OpenAI, Langchain, Azure Cognitive Services",
              },
            ],
          },
        ],
        images: ["/images/case-study/revlyx.png"],
        layout: "text-top",
      },
      {
        title: "3. Project Wildling – Seller Marketplace Takealot Platform",
        paragraphs: [
          {
            text: "Nujoom AI is an innovative AI SaaS platform that transforms uploaded documents—such as PDFs, invoices, and stories—into intelligent, voice-interactive chatbots. The core of the product lies in its real-time speech-to-speech interaction, enabling users to talk to their AI chatbot and receive spoken answers. With a design inspired by natural human conversations, Nujoom AI offers a new way to interact with document-based information, much like speaking to a smart virtual assistant during a meeting.",
            bullets: [
              {
                text: "IIndustry / Client Type: AI SaaS / Productivity & Voice Tech",
              },
              {
                text: "Project Type: AI Chatbot Builder · Voice AI · File Intelligence Platform",
              },
              {
                text: "Duration: 2.5 Months",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB, OpenAI, Langchain, Azure Cognitive Services",
              },
            ],
          },
        ],
        images: ["/images/case-study/rentu_pay.png"],
        layout: "text-top",
      },
      {
        title: "4. Dental Kramer – Seller Marketplace Takealot Platform",
        paragraphs: [
          {
            text: "Nujoom AI is an innovative AI SaaS platform that transforms uploaded documents—such as PDFs, invoices, and stories—into intelligent, voice-interactive chatbots. The core of the product lies in its real-time speech-to-speech interaction, enabling users to talk to their AI chatbot and receive spoken answers. With a design inspired by natural human conversations, Nujoom AI offers a new way to interact with document-based information, much like speaking to a smart virtual assistant during a meeting.",
            bullets: [
              {
                text: "IIndustry / Client Type: AI SaaS / Productivity & Voice Tech",
              },
              {
                text: "Project Type: AI Chatbot Builder · Voice AI · File Intelligence Platform",
              },
              {
                text: "Duration: 2.5 Months",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB, OpenAI, Langchain, Azure Cognitive Services",
              },
            ],
          },
        ],
        images: ["/images/case-study/aifornia.png"],
        layout: "text-top",
      },
    ],
  },
  llmops: {
    category: "Software Development",
    title: "Education & E-Learning Platforms",
    sections: [
      {
        paragraphs: [
          {
            text: "Empower learners and educators with smart, scalable EdTech solutions. ZYSOFTEC develops online learning platforms, LMS systems, and virtual classrooms that make education accessible, interactive, and trackable.",
          },
          {
            text: "Key education features:",
            bullets: [
              {
                text: "Course creation and progress tracking",
              },
              {
                text: "Virtual classrooms and live sessions",
              },
              {
                text: "Assessments, quizzes, and certification tools",
              },
              {
                text: "Student and teacher dashboards",
              },
              {
                text: "Mobile learning support",
              },
            ],
          },
          {
            text: "Educational projects include:",
            bullets: [
              // {
              //   text: "Education Site – Online learning with assessments",
              // },
              {
                text: "OrgUpdate – Training & research platform",
              },
              {
                text: "CV Breakthrough – Career mentorship and guidance portal",
              },
            ],
          },
          {
            text: "Whether for a training institute, university, or solo educator, we build education systems that scale with your mission.",
          },
        ],
        layout: "text-only",
      },
     
      {
        title: "1. Org Update – Data Extraction & Visualization",
        paragraphs: [
          {
            text: "Nujoom AI is an innovative AI SaaS platform that transforms uploaded documents—such as PDFs, invoices, and stories—into intelligent, voice-interactive chatbots. The core of the product lies in its real-time speech-to-speech interaction, enabling users to talk to their AI chatbot and receive spoken answers. With a design inspired by natural human conversations, Nujoom AI offers a new way to interact with document-based information, much like speaking to a smart virtual assistant during a meeting.",
            bullets: [
              {
                text: "IIndustry / Client Type: AI SaaS / Productivity & Voice Tech",
              },
              {
                text: "Project Type: AI Chatbot Builder · Voice AI · File Intelligence Platform",
              },
              {
                text: "Duration: 2.5 Months",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB, web scrapper",
              },
            ],
          },
        ],
        images: ["/images/case-study/case1.svg"],
        layout: "text-top",
      },
      {
        title: "2. CV Breakthrough – CV Submission Platform and Hiring",
        paragraphs: [
          {
            text: "CV Breakthrough is a straightforward web application that enables candidates to upload their resumes and allows recruiters to search and connect with applicants. Built without complex AI or standalone portals, it focuses on core functionality and a clean user experience. Both parties can manage documents and communications through a single, browser‑based interface.            ",
            bullets: [
              {
                text: "Resume Upload & Managemen",
              },
              {
                text: "Recruiter Search",
              },
              {
                text: "Hire candidate & email automated",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB",
              },
            ],
          },
        ],
        // images: ["/images/case-study/aifornia.png"],
        layout: "text-top",
      },
    ],
  },
  devops: {
    category: "Software Development",
    title: "Website UI/UX Design",
    sections: [
      {
        paragraphs: [
          {
            text: "At ZYSOFTEC, we specialize in crafting modern, user-focused website designs that deliver both beauty and functionality. Our websites are responsive, performance-optimized, and tailored to enhance user journeys across all screen sizes.",
          },
          {
            text: "We design with a clear focus on conversion-driven layouts, intuitive navigation, and brand consistency, ensuring your online presence stands out. From corporate websites and e-commerce platforms to SaaS dashboards, we create web experiences that not only attract but also retain and convert users.",
          },
        ],
        images: ["/images/case-study/nb-web.png"],
        layout: "text-only",
      },
      {
        paragraphs: [],
        images: ["/images/case-study/rentu_pay.png"],
        layout: "text-only",
      },
      {
        paragraphs: [],
        images: ["/images/case-study/denka_web.png"],
        layout: "text-only",
      },
      {
        paragraphs: [],
        images: ["/images/case-study/club_foot.png"],
        layout: "text-only",
      },
      {
        paragraphs: [],
        images: ["/images/case-study/case2.svg"],
        layout: "text-only",
      },
      {
        paragraphs: [],
        images: ["/images/case-study/case1.svg"],
        layout: "text-only",
      },
      // {
      //   title: "1. Website UI UX ",
      //   paragraphs: [
      //     {
      //       text: "Nujoom AI is an innovative AI SaaS platform that transforms uploaded documents—such as PDFs, invoices, and stories—into intelligent, voice-interactive chatbots. The core of the product lies in its real-time speech-to-speech interaction, enabling users to talk to their AI chatbot and receive spoken answers. With a design inspired by natural human conversations, Nujoom AI offers a new way to interact with document-based information, much like speaking to a smart virtual assistant during a meeting.",
      //       bullets: [
      //         {
      //           text: "Industry / Client Type: AI SaaS / Productivity & Voice Tech",
      //         },
      //         {
      //           text: "Project Type: AI Chatbot Builder · Voice AI · File Intelligence Platform",
      //         },
      //         {
      //           text: "Duration: 2.5 Months",
      //         },
      //         {
      //           text: "Technologies Used: Next.js, Node.js, MongoDB, OpenAI, Langchain, Azure Cognitive Services",
      //         },
      //       ],
      //     },
      //   ],
      //   images: ["/images/case-study/aifornia.png"],
      //   layout: "text-top",
      // },
    ],
  },

  "vr-ar-mr": {
    category: "Software Development",
    title: "App UI/UX Design",
    sections: [
      {
        paragraphs: [
          {
            text: "We design mobile app interfaces that are clean, intuitive, and optimized for maximum user engagement on iOS and Android platforms. Our mobile design process includes user flow architecture, interactive prototyping, and scalable design systems tailored to your product’s needs.",
          },
          {
            text: "Whether you're launching a fintech solution, lifestyle app, or productivity tool, ZYSOFTEC ensures a seamless app experience that reflects your brand and delivers high retention through superior usability and design clarity.",
          },
        ],
        images: ["/images/case-study/soulmatch_app.png"],
       
        layout: "text-only",
      },
      {
        paragraphs: [],
        images: ["/images/case-study/seller_app.png"],
        layout: "text-only",
      },
      {
        paragraphs: [],
        images: ["/images/case-study/txtract.png"],
        layout: "text-only",
      },
      
    ],
  },

  iot: {
    category: "Software Development",
    title: "Dashboard UI/UX",
    sections: [
      {
        paragraphs: [
          {
            text: "ZYSOFTEC brings your digital product ideas to life through a strategic, user-first design process. We start with UX research and wireframing, progress through interactive prototyping, and finalize with high-fidelity UI design tailored to your product’s goals.",
          },
          {
            text: "From SaaS platforms and AI tools to custom dashboards and web applications, our product design services combine functionality, aesthetic value, and scalability, ensuring your product delivers real value and a delightful user experience.",
          },
        ],
        images: ["/images/case-study/nb_crm.png"],
        layout: "text-only",
      },
      {
        paragraphs: [],
        images: ["/images/case-study/one_union.png"],
        layout: "text-only",
      },
      {
        paragraphs: [],
        images: ["/images/case-study/soul.png"],
        layout: "text-only",
      },
      {
        paragraphs: [],
        images: ["/images/case-study/denka_crm.png"],
        layout: "text-only",
      },
    ],
  },

  "market-research": {
    category: "Software Development",
    title: "Creatives",
    sections: [
      {
        paragraphs: [
          {
            text: "We design high-impact digital creatives that elevate your brand across every digital touchpoint—from social media graphics and marketing banners to sales presentations and visual content for ad campaigns.",
          },
          {
            text: "At ZYSOFTEC, our creative team merges visual storytelling with brand strategy to produce content that captures attention, communicates clearly, and converts effectively. Every asset is optimized for its platform, ensuring consistency and engagement across your marketing channels.",
          },
        ],
        layout: "text-only",
      },
      {
        title: "Social media posts ",
        paragraphs: [
          {
            text: "Nujoom AI is an innovative AI SaaS platform that transforms uploaded documents—such as PDFs, invoices, and stories—into intelligent, voice-interactive chatbots. The core of the product lies in its real-time speech-to-speech interaction, enabling users to talk to their AI chatbot and receive spoken answers. With a design inspired by natural human conversations, Nujoom AI offers a new way to interact with document-based information, much like speaking to a smart virtual assistant during a meeting.",
            bullets: [
              {
                text: "IIndustry / Client Type: AI SaaS / Productivity & Voice Tech",
              },
              {
                text: "Project Type: AI Chatbot Builder · Voice AI · File Intelligence Platform",
              },
              {
                text: "Duration: 2.5 Months",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB, OpenAI, Langchain, Azure Cognitive Services",
              },
            ],
          },
        ],
        images: ["/images/case-study/aifornia.png"],
        layout: "text-top",
      },
      {
        title: "2. Videos",
        paragraphs: [
          {
            text: "Nujoom AI is an innovative AI SaaS platform that transforms uploaded documents—such as PDFs, invoices, and stories—into intelligent, voice-interactive chatbots. The core of the product lies in its real-time speech-to-speech interaction, enabling users to talk to their AI chatbot and receive spoken answers. With a design inspired by natural human conversations, Nujoom AI offers a new way to interact with document-based information, much like speaking to a smart virtual assistant during a meeting.",
            bullets: [
              {
                text: "IIndustry / Client Type: AI SaaS / Productivity & Voice Tech",
              },
              {
                text: "Project Type: AI Chatbot Builder · Voice AI · File Intelligence Platform",
              },
              {
                text: "Duration: 2.5 Months",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB, OpenAI, Langchain, Azure Cognitive Services",
              },
            ],
          },
        ],
        images: ["/images/case-study/aifornia.png"],
        layout: "text-top",
      },
    ],
  },

  "customer-experience": {
    category: "Software Development",
    title: "Logo & Branding",
    sections: [
      {
        paragraphs: [
          {
            text: "ZYSOFTEC builds strong brand identities starting with distinctive, meaningful logos and extending to comprehensive branding systems. Our process includes the development of visual identity, color palette selection, typography guidelines, and brand style guides to ensure consistency across web, mobile, and print platforms.",
          },
          {
            text: "Whether you're a startup creating your first identity or a business refreshing your look, we deliver branding solutions that resonate with your audience and differentiate you in the market, building trust, recognition, and long-term loyalty.",
          },
        ],
        layout: "text-only",
      },
      {
        title: "Logo ",
        paragraphs: [
          {
            text: "Nujoom AI is an innovative AI SaaS platform that transforms uploaded documents—such as PDFs, invoices, and stories—into intelligent, voice-interactive chatbots. The core of the product lies in its real-time speech-to-speech interaction, enabling users to talk to their AI chatbot and receive spoken answers. With a design inspired by natural human conversations, Nujoom AI offers a new way to interact with document-based information, much like speaking to a smart virtual assistant during a meeting.",
            bullets: [
              {
                text: "IIndustry / Client Type: AI SaaS / Productivity & Voice Tech",
              },
              {
                text: "Project Type: AI Chatbot Builder · Voice AI · File Intelligence Platform",
              },
              {
                text: "Duration: 2.5 Months",
              },
              {
                text: "Technologies Used: Next.js, Node.js, MongoDB, OpenAI, Langchain, Azure Cognitive Services",
              },
            ],
          },
        ],
        images: ["/images/case-study/aifornia.png"],
        layout: "text-top",
      },
    ],
  },

  // Features/Services Section Modals
  "custom-software": {
    category: "Software Development",
    title: "Custom Software Solutions",
    subtitle: "Tailored applications that perfectly fit your business needs.",
    sections: [
      {
        title: "What We Build",
        paragraphs: [
          {
            text: "We create custom software solutions that streamline your operations and boost productivity.",
            isBold: true,
            bullets: [
              { text: "Enterprise Resource Planning (ERP)" },
              { text: "Customer Relationship Management (CRM)" },
              { text: "Inventory Management Systems" },
              { text: "Workflow Automation Tools" },
            ],
          },
        ],
        images: ["/images/truck1.jpg"],
        layout: "text-top",
      },
      {
        title: "Development Process",
        paragraphs: [
          {
            text: "Our proven development methodology ensures successful project delivery.",
            bullets: [
              { text: "Requirements Gathering & Analysis" },
              { text: "System Design & Architecture" },
              { text: "Agile Development & Testing" },
              { text: "Deployment & Training" },
            ],
          },
        ],
        layout: "text-only",
      },
    ],
  },

  "ai-ml-solutions": {
    category: "Artificial Intelligence",
    title: "AI & Machine Learning",
    subtitle: "Intelligent solutions that learn and adapt to your business.",
    sections: [
      {
        title: "AI Capabilities",
        paragraphs: [
          {
            text: "Leverage the power of artificial intelligence to automate processes and gain valuable insights.",
            isBold: true,
            bullets: [
              { text: "Predictive Analytics" },
              { text: "Natural Language Processing" },
              { text: "Computer Vision & Image Recognition" },
              { text: "Chatbots & Virtual Assistants" },
            ],
          },
        ],
        images: ["/images/truck2.jpg"],
        layout: "image-top",
      },
    ],
  },

  "cloud-solutions": {
    category: "Cloud Services",
    title: "Cloud Infrastructure",
    subtitle: "Scalable, secure, and reliable cloud solutions.",
    sections: [
      {
        title: "Cloud Services",
        paragraphs: [
          {
            text: "We help businesses migrate to the cloud and optimize their infrastructure for maximum performance.",
            isBold: true,
            bullets: [
              { text: "AWS, Azure, Google Cloud" },
              { text: "Cloud Migration & Optimization" },
              { text: "DevOps & CI/CD Pipelines" },
              { text: "Security & Compliance" },
            ],
          },
        ],
        images: ["/images/truck3.jpg"],
        layout: "text-image",
      },
    ],
  },

  // Portfolio/Our Work Section Modals
  "saas-platform": {
    category: "Portfolio",
    title: "SaaS Platform Development",
    subtitle: "A comprehensive SaaS solution for business management.",
    sections: [
      {
        title: "Project Overview",
        paragraphs: [
          {
            text: "Developed a full-featured SaaS platform that helps businesses manage their operations efficiently.",
            isBold: true,
            bullets: [
              { text: "Multi-tenant Architecture" },
              { text: "Real-time Analytics Dashboard" },
              { text: "Payment Integration" },
              { text: "Mobile App Support" },
            ],
          },
        ],
        images: ["/images/truck1.jpg"],
        layout: "text-top",
      },
      {
        title: "Technologies Used",
        paragraphs: [
          {
            text: "Built with modern technologies for scalability and performance.",
            bullets: [
              { text: "React.js & Node.js" },
              { text: "PostgreSQL Database" },
              { text: "AWS Cloud Infrastructure" },
              { text: "Docker & Kubernetes" },
            ],
          },
        ],
        layout: "text-only",
      },
    ],
  },

  "ai-application": {
    category: "Portfolio",
    title: "AI-Powered Application",
    subtitle: "Intelligent automation solution for enterprise clients.",
    sections: [
      {
        title: "AI Features",
        paragraphs: [
          {
            text: "Created an AI application that automates complex business processes and provides intelligent insights.",
            isBold: true,
            bullets: [
              { text: "Machine Learning Models" },
              { text: "Natural Language Processing" },
              { text: "Predictive Analytics" },
              { text: "Real-time Data Processing" },
            ],
          },
        ],
        images: ["/images/truck2.jpg"],
        layout: "image-top",
      },
    ],
  },

  "ecommerce-platform": {
    category: "Portfolio",
    title: "E-commerce Platform",
    subtitle: "Modern online shopping experience with advanced features.",
    sections: [
      {
        title: "Platform Features",
        paragraphs: [
          {
            text: "Built a comprehensive e-commerce platform with advanced features and excellent user experience.",
            isBold: true,
            bullets: [
              { text: "Multi-vendor Marketplace" },
              { text: "Advanced Search & Filters" },
              { text: "Payment Gateway Integration" },
              { text: "Inventory Management" },
            ],
          },
        ],
        images: ["/images/truck3.jpg"],
        layout: "text-image",
      },
    ],
  },

  "web-development-portfolio": {
    category: "Portfolio",
    title: "Web Development",
    subtitle:
      "We build responsive, secure, and scalable web applications tailored to your goals. Using modern tech stacks like MERN and Next.js, our web solutions are optimized for performance, usability, and future growth.",
    sections: [
      {
        title: "Web Development (MERN Stack)",
        paragraphs: [
          {
            text: "At ZYSOFTEC, we specialize in building high-performance full-stack web applications using the advanced MERN stack — MongoDB, Express.js, React.js/Next.js, and Node.js. Our team of expert developers creates secure, scalable, and fast-loading digital platforms tailored to meet the evolving needs of startups, SMEs, and enterprises",
          },
          {
            text: "From user-friendly interfaces to complex backend systems, we deliver complete web solutions that are SEO-optimized and future-ready.",
          },
          {
            text: "Our Web Development Expertise Includes:",
            isBold: true,
            bullets: [
              {
                text: "Modern Frontend Development using React.js and Next.js for responsive and interactive UIs",
              },
              { text: "Powerful Backend APIs with Node.js and Express.js" },
              {
                text: "Role-based Access Control, JWT authentication, and OAuth integration",
              },
              {
                text: "Real-Time Features with WebSockets and socket.io",
              },
              {
                text: "Secure Payment Integrations (e.g., Stripe, PayPal, Razorpay)",
              },
              {
                text: "CRM, ERP & SaaS Development",
              },
              {
                text: "Third-party Integrations (Zapier, CRMs, analytics, and more)",
              },
              {
                text: "SEO-optimized Routing and Performance Tuning with Next.js",
              },
            ],
          },
          {
            text: "Whether you’re looking to build a SaaS platform, custom web portal, admin dashboard, or CRM system, we craft digital products that drive real business value.",
          },
        ],
      },
      {
        title: "Projects We've Delivered",
        paragraphs: [
          {
            text: "Neumandate – Smart Tax Consulting Platform",
            isBold: true,
          },
          {
            text: "A fully automated web platform for tax consultants, Neumandate enables seamless custom offer generation, client onboarding, and Stripe-based billing. The system supports dynamic PDF generation, digital contract signing, and print-ready proposals, empowering tax advisors to operate with enhanced efficiency and compliance.",
          },
          {
            text: "SellerMetrics – E-commerce Analytics Dashboard",
            isBold: true,
          },
          {
            text: "An advanced analytics solution built for online sellers. SellerMetrics provides real-time insights into Buy Box tracking, financial metrics, interactive graphs, and custom dashboards. Designed for performance and clarity, it helps e-commerce businesses make informed, data-driven decisions.",
          },
          {
            text: "Fiksuu – Digital Business Continuity Panel",
            isBold: true,
          },
          {
            text: "Fiksuu is a modern web panel for business continuity and resilience planning. It supports multi-role user management (admin, business user, public user), with four types of digital business profiles. Features include Zapier integration, lead capture, user analytics, and a powerful back-office dashboard.",
          },
          {
            text: "SoulMatch – Relationship Logic & Matching System",
            isBold: true,
          },
          {
            text: "A smart matchmaking platform that uses behavioral logic and preference-based algorithms to connect users. With real-time live chat, profile setup, partner preferences, and request management, SoulMatch is available as both a mobile app and an admin panel for full platform control..",
          },
          {
            text: "Undiscovered – Creative Talent Discovery Platform",
            isBold: true,
          },
          {
            text: "Undiscovered is a vibrant online platform connecting undiscovered creators with talent seekers. Built with a sleek UI, the site enables creators to showcase portfolios, engage in challenges, and gain visibility. The admin dashboard supports profile moderation, content curation, and analytics — all built with blazing-fast performance using the MERN stack.",
          },
          {
            text: "Denka – Customer Relationship Management System",
            isBold: true,
          },
          {
            text: "Denka is a powerful CRM and internal business operations platform designed to manage leads, communication, tasks, and user roles. Featuring contact history tracking, team collaboration tools, custom pipelines, and secure data handling, Denka streamlines workflows and drives productivity across departments",
          },
        ],
      },
    ],
  },
  "mobile-development-portfolio": {
    category: "Portfolio",
    title: "Mobile App Development",
    subtitle:
      "From iOS to Android and cross-platform apps, we develop mobile solutions that deliver smooth, high-performance user experiences. Whether it’s a startup MVP or enterprise-grade app, we’ve got you covered — from design to deployment.",
    sections: [
      {
        title: "Mobile App Development",
        paragraphs: [
          {
            text: "At ZYSOFTEC, we design and develop custom mobile applications that combine cutting-edge functionality with elegant UI/UX—ensuring smooth, high-performance experiences across iOS and Android devices. Whether you’re launching a Minimum Viable Product (MVP) or scaling an existing solution, we build apps that captivate users and drive business outcomes.",
          },
          {
            text: "Technologies We Use:",
            isBold: true,
            bullets: [
              {
                text: "Cross-Platform Frameworks: React Native & Flutter for cost-effective, single-codebase apps",
              },
              {
                text: "Cloud-Powered Features: Real-time sync, push notifications, offline access, and cloud APIs",
              },
              {
                text: "Backend Infrastructure: Firebase, AWS Amplify, or fully custom Node.js/Express APIs",
              },
              {
                text: "User Security: JWT-based authentication, secure data storage, GDPR-compliant architecture",
              },
              {
                text: "3rd-Party Integrations: Payment gateways, mapping, analytics, CRMs, and external SDKs",
              },
            ],
          },
          {
            text: "From e-commerce platforms to healthcare and productivity tools, our mobile apps are user-focused, secure, and designed for visibility on the App Store and Google Play.",
          },
        ],
      },
      {
        title: "Featured Mobile Projects",
        paragraphs: [
          {
            text: "Les Veggies App – Grocery Ordering Platform",
            isBold: true,
          },
          {
            text: "A sleek and intuitive mobile app for grocery and product ordering, Les Veggies enables users to browse categories, add items to cart, and place orders with ease. Built for performance and convenience, the app includes real-time inventory sync, order tracking, and secure payment integration, making grocery shopping faster than ever.",
          },
          {
            text: "SoulMatch – Relationship Discovery App",
            isBold: true,
          },
          {
            text: "SoulMatch is a behavior-based relationship discovery mobile app that goes beyond swiping. With personality matching, user profiles, in-app chat, and request logic, the app helps users connect in more meaningful ways. Designed with scalable architecture and real-time communication, it ensures seamless and secure user interaction.",
          },
          {
            text: "Txtract – Document & Data Extraction App",
            isBold: true,
          },
          {
            text: "Txtract simplifies document scanning, OCR-based data extraction, and file conversion into usable digital formats. Ideal for business workflows, the app features camera-based capture, PDF export, secure cloud sync, and custom parsing rules for extracting key information quickly and accurately.",
          },
          {
            text: "Eventbright – Smart Event Sync App",
            isBold: true,
          },
          {
            text: "Eventbright integrates with three major APIs to deliver real-time event updates, calendar sync, and ticket management. Designed for both attendees and organizers, it enables smooth scheduling, RSVP tracking, and event discovery—all from a mobile-first interface.",
          },
          {
            text: "Finance App – E-commerce Insights & Analytics",
            isBold: true,
          },
          {
            text: "A mobile-first analytics tool offering deep insights into Buy Box performance, financial tracking, and data visualization. The Finance App includes interactive dashboards, graph-based trend analysis, and custom metrics tracking, empowering e-commerce sellers with decision-making data on the go.",
          },
          {
            text: "Job Portal App – Career Platform",
            isBold: true,
          },
          {
            text: "This job portal mobile app connects job seekers with employers via a modern and intuitive UI. Key features include advanced job search filters, profile creation, resume uploads, application tracking, and push notifications for updates. Built with a focus on accessibility, speed, and retention, it's designed to optimize career discovery for mobile users.",
          },
          {
            text: "Healthcare App – Medical Appointments & Records",
            isBold: true,
          },
          {
            text: "A user-centric healthcare solution that offers appointment scheduling, medical record storage, prescription tracking, and doctor-patient chat. With built-in reminders, HIPAA-compliant data handling, and real-time updates, the app empowers users to take control of their health while helping providers streamline patient management.",
          },
        ],
      },
    ],
  },
  "ui-ux-design-portfolio": {
    category: "Portfolio",
    title: "UI UX Design",
    subtitle:
      "We craft intuitive and user-focused UI/UX designs that enhance engagement and usability across web and mobile platforms. Our design approach ensures seamless navigation, responsive layouts, and visually compelling interfaces.",
    sections: [
      {
        title: "UI/UX Design Services",
        paragraphs: [
          {
            text: "At ZYSOFTEC, we specialize in crafting intuitive and user-friendly digital experiences. Our UI/UX design services focus on solving real user problems through empathetic research, clean visuals, and conversion-oriented flows. We bring your ideas to life with interactive prototypes, seamless interfaces, and design systems that scale.",
          },
          {
            text: "Our UI/UX Design Process Includes:",
            isBold: true,
            bullets: [
              {
                text: "User Research & Persona Development",
              },
              {
                text: "Wireframes & Information Architecture",
              },
              {
                text: "Prototyping with Figma (Interactive & Clickable)",
              },
              {
                text: "Feedback-Driven Usability Testing",
              },
              {
                text: "Mobile-Responsive & Web-Optimized Layouts",
              },
            ],
          },
          {
            text: "Whether you're launching a new mobile app, internal dashboard, or SaaS platform, our UX-first approach improves user engagement, increases retention, and enhances brand credibility.",
          },
        ],
      },
      {
        title: "Featured UI/UX Projects",
        paragraphs: [
          {
            text: "SoulMatch App – Next-Gen Matrimonial Matching App",
            isBold: true,
          },
          {
            text: "A modern UI crafted for a smart matrimony platform. Features include detailed profile onboarding, behavioral-based partner preferences, clean match cards, and in-app messaging. The design emphasizes emotional connection through soft tones, clear layouts, and minimal distraction.",
          },
          {
            text: "Influentcity – Influencer & Brand Collaboration Platform",
            isBold: true,
          },
          {
            text: "A dynamic interface for connecting influencers with brands. The design includes dashboards for campaign tracking, profile monetization, and proposal management. With modern visuals and data-driven elements, the platform encourages collaboration with clarity and style.",
          },
          {
            text: "Cybersecurity Dashboard – Inturity",
            isBold: true,
          },
          {
            text: "A highly functional cybersecurity admin dashboard focused on threat monitoring, vulnerability tracking, and compliance metrics. The interface uses a dark theme for focus, includes real-time visualizations, and supports role-based modules for analysts and admins.",
          },
          {
            text: "Les Veggies App – Grocery Delivery Platform",
            isBold: true,
          },
          {
            text: "A clean, user-friendly UI designed for seamless grocery ordering. From browsing categories to smooth checkout, the design features intuitive navigation, eye-catching product cards, and simplified cart management—all optimized for mobile use.",
          },
          {
            text: "Salon App – Kutsbe Landing Page & Booking System",
            isBold: true,
          },
          {
            text: "Designed for salons and personal care services, this elegant UI blends productized services with appointment scheduling. The landing page uses soft visuals and structured CTAs to drive engagement and bookings, enhancing both trust and usability.",
          },
        ],
      },
      {
        title: "Why Brands Trust Our UI/UX Team",
        paragraphs: [
          {
            text: "",
            isBold: true,
            bullets: [
              {
                text: "Mobile-First & Conversion-Ready Designs",
              },
              {
                text: "Collaborative, Transparent Figma Workflows",
              },
              {
                text: "Fully Testable Prototypes for Feedback",
              },
              {
                text: "Adaptive, Brand-Aligned Visuals",
              },
              {
                text: "Expertise Across Apps, Dashboards & Web Tools",
              },
            ],
          },
          {
            text: "Let ZYSOFTEC design your next product the right way—strategic, engaging, and human-centered.",
          },
        ],
      },
    ],
  },
  "cms-portfolio": {
    category: "Portfolio",
    title: "CMS",
    subtitle:
      "We develop powerful CMS solutions using WordPress and Shopify, tailored for seamless content management and high-performance websites. Our CMS services focus on scalability, SEO optimization, and user-friendly interfaces to drive engagement and business growth.",
    sections: [
      {
        title: "CMS Development (WordPress & Shopify)",
        paragraphs: [
          {
            text: "At ZYSOFTEC, we build custom CMS websites that are powerful, flexible, and easy to manage. Whether you're running a content-heavy blog, a service-based business, or a high-converting eCommerce store, our team ensures a seamless experience with WordPress and Shopify platforms. We focus on delivering mobile-first, SEO-ready, and performance-optimized solutions tailored to your goals.",
          },
          {
            text: "Our CMS Development Services Include:",
            isBold: true,
            bullets: [
              {
                text: "Custom WordPress theme and Shopify template development",
              },
              {
                text: "Elementor-based visual editing and custom page builder setups",
              },
              {
                text: "Plugin and app integration for added functionality",
              },
              {
                text: "Product catalog setup and management (Shopify)",
              },
              {
                text: "Multilingual support, dynamic blog systems, and FAQs",
              },
              {
                text: "SEO-friendly markup, schema integration, and fast loading performance",
              },
            ],
          },
          {
            text: "From backend simplicity to frontend elegance, our CMS solutions are designed to grow with your business.",
          },
        ],
      },
      {
        title: "Featured CMS Projects",
        paragraphs: [
          {
            text: "Dental Kramer – WordPress (Elementor)",
            isBold: true,
          },
          {
            text: "A modern, clean, and professional website built for a dental clinic, Dental Kramer highlights essential services, treatment categories, and patient-friendly appointment booking. Developed using WordPress + Elementor, the site offers smooth navigation, strong mobile responsiveness, and SEO-rich content, helping the clinic attract local patients effectively",
          },
          {
            text: "MySportia – Custom PHP on WordPress",
            isBold: true,
          },
          {
            text: "MySportia is a fully customized sports platform built with advanced PHP functionality on WordPress. Tailored to meet unique business needs, it features dynamic content areas, custom post types, and interactive modules beyond typical WordPress templates. The solution delivers both performance and flexibility for a growing digital brand.",
          },
          {
            text: "Starlink Tech – Elementor WordPress Site",
            isBold: true,
          },
          {
            text: "A technology-focused business website, Starlink Tech was developed using WordPress and Elementor for a sleek, tech-savvy feel. The layout emphasizes service descriptions, client trust elements, and responsive sections. Optimized for both desktop and mobile users, the site supports clear CTAs and performance-driven SEO architecture",
          },
        ],
      },
      {
        title: "Why Choose ZYSOFTEC for CMS Development?",
        paragraphs: [
          {
            text: "",
            isBold: true,
            bullets: [
              {
                text: "Custom Design + Easy Editing: Our Elementor and Shopify builds let you manage content without coding.",
              },
              {
                text: "Scalable Architecture: Ready to grow as your traffic and offerings expand.",
              },
              {
                text: "SEO-Optimized: From schema to speed, your site is built to rank.",
              },
              {
                text: "Secure & Fast: Performance tuning, HTTPS, and backend hardening included.",
              },
            ],
          },
          {
            text: "Let ZYSOFTEC design your next product the right way—strategic, engaging, and human-centered.",
          },
        ],
      },
    ],
  },
  "ai-llm-integrations-portfolio": {
    category: "Portfolio",
    title: "AI, LLM & AI Integrations",
    subtitle:
      "Leverage the power of AI and large language models to enhance automation, personalization, and decision-making. We integrate cutting-edge AI tools into your software products — from GPT-based chatbots to custom ML pipelines — tailored to your business.",
    sections: [
      {
        title: "AI, LLM & AI Integrations",
        paragraphs: [
          {
            text: "Smart Solutions Powered by Artificial Intelligence",
            isBold: true,
          },
          {
            text: "At ZYSOFTEC, we integrate powerful AI and Large Language Models (LLMs) into your software to boost automation, personalization, and intelligent decision-making. Whether it's a GPT-based chatbot, a custom ML model, or AI-driven automation, we tailor every solution to your business needs.",
          },
          {
            text: "What We Offer:",
            isBold: true,
            bullets: [
              {
                text: "GPT Chatbots & Virtual Assistants – Automate support and internal tasks with human-like AI chat.",
              },
              {
                text: "Machine Learning Models – Predict trends, detect fraud, and personalize user experiences.",
              },
              {
                text: "NLP Tools – Summarize, classify, and analyze text data with accuracy.",
              },
              {
                text: "AI-Powered Automation – Streamline repetitive tasks with intelligent workflows.",
              },
              {
                text: "Vision AI – Image recognition and object detection for smarter systems.",
              },
            ],
          },
        ],
      },
      {
        title: "Platforms We Use:",
        paragraphs: [
          {
            text: "OpenAI, Hugging Face, LangChain, TensorFlow, PyTorch, AWS AI, and more.",
          },
        ],
      },
    ],
  },
  "logo-branding-portfolio": {
    category: "Portfolio",
    title: "Logo & Branding",
    subtitle:
      "We craft impactful logo and branding solutions that strengthen your visual identity and resonate with your target audience. Our designs are rooted in strategy, creativity, and consistency—helping your brand stand out across all digital and print platforms.",
    sections: [
      {
        paragraphs: [
          {
            text: "ZYSOFTEC builds strong brand identities starting with distinctive, meaningful logos and extending to comprehensive branding systems. Our process includes visual identity development, color palette selection, typography guidelines, and brand style guides to ensure consistency across web, mobile, and print.",
            isBold: true,
          },
          {
            text: "Whether you're a startup creating your first identity or a business refreshing your look, we deliver branding solutions that resonate with your audience and differentiate you in the market—building trust, recognition, and long-term loyalty.",
          },
        ],
      },
    ],
  },

  // Comprehensive Modal Showcase - All Layouts and Features
  default: {
    category: "Showcase",
    title: "Complete Modal Design System",
    subtitle:
      "Explore all available layouts, content types, and design possibilities for creating rich, engaging modal experiences.",
    sections: [
      {
        title: "Text-Only Layout with Rich Content",
        paragraphs: [
          {
            text: "This section demonstrates a text-only layout with comprehensive content formatting options. You can create engaging, informative content without images while maintaining visual hierarchy and readability.",
            isBold: true,
          },
          {
            text: "Our modal system supports various text formatting options including bold text, bullet points with links, and embedded buttons. This makes it perfect for detailed service descriptions, documentation, or feature explanations.",
            bullets: [
              {
                text: "Rich text formatting with bold, italic, and emphasis options",
                links: [
                  { text: "Learn more about formatting", url: "#formatting" },
                ],
              },
              {
                text: "Bullet points with embedded links for easy navigation",
                links: [
                  { text: "Documentation", url: "#docs" },
                  { text: "Examples", url: "#examples" },
                ],
              },
              {
                text: "Multiple paragraphs with proper spacing and typography",
              },
              { text: "Professional content structure for better readability" },
            ],
          },
          {
            text: "You can also include call-to-action buttons directly within paragraphs to guide users to specific actions or external resources.",
            buttons: [
              { text: "Get Started", variant: "primary" },
              { text: "View Documentation", variant: "secondary" },
            ],
          },
        ],
        layout: "text-only",
      },
      {
        title: "Text-Image Layout (Text First)",
        paragraphs: [
          {
            text: "The text-image layout places text content first, followed by supporting images. This is ideal when you want to lead with information and use visuals to reinforce your message.",
            isBold: true,
          },
          {
            text: "This layout works perfectly for:",
            bullets: [
              { text: "Service descriptions with supporting screenshots" },
              { text: "Feature explanations with visual examples" },
              { text: "Process workflows with step-by-step images" },
              { text: "Product demonstrations with interface mockups" },
            ],
          },
          {
            text: "The text-image layout ensures that users read your content first, then see the visual reinforcement. This creates a logical flow that enhances understanding and engagement.",
            links: [
              { text: "View more examples", url: "#examples" },
              { text: "Learn about best practices", url: "#best-practices" },
            ],
          },
        ],
        images: ["/public/logo.png", "/public/globe.svg"],
        layout: "text-image",
      },
      {
        title: "Image-Text Layout (Image First)",
        paragraphs: [
          {
            text: "The image-text layout leads with visual content, making it perfect for showcasing designs, mockups, or visual concepts before diving into detailed explanations.",
            isBold: true,
          },
          {
            text: "This layout is particularly effective for:",
            bullets: [
              { text: "Portfolio showcases and project presentations" },
              { text: "UI/UX design demonstrations" },
              { text: "Product mockups and prototypes" },
              { text: "Visual storytelling and case studies" },
            ],
          },
          {
            text: "By leading with visuals, you capture attention immediately and then provide context through detailed text content. This approach works well for creative and design-focused content.",
            buttons: [
              { text: "View Portfolio", variant: "primary" },
              { text: "Request Quote", variant: "secondary" },
            ],
          },
        ],
        images: ["/public/file.svg", "/public/window.svg"],
        layout: "image-text",
      },
      {
        title: "Text-Top Layout with Multiple Images",
        paragraphs: [
          {
            text: "The text-top layout places all text content at the top, followed by a gallery of images below. This is excellent for comprehensive explanations with multiple visual examples.",
            isBold: true,
          },
          {
            text: "This layout excels at:",
            bullets: [
              {
                text: "Comprehensive service overviews with multiple examples",
              },
              { text: "Feature comparisons with visual aids" },
              { text: "Step-by-step processes with multiple screenshots" },
              { text: "Before/after comparisons and transformations" },
            ],
          },
          {
            text: "The text-top layout allows you to provide complete context before showing visual examples, ensuring users understand the full scope before examining specific details.",
            links: [
              { text: "Download case study", url: "#case-study" },
              { text: "Schedule consultation", url: "#consultation" },
            ],
          },
        ],
        images: [
          "/public/logo.png",
          "/public/globe.svg",
          "/public/file.svg",
          "/public/window.svg",
        ],
        layout: "text-top",
      },
      {
        title: "Image-Top Layout for Visual Impact",
        paragraphs: [
          {
            text: "The image-top layout creates maximum visual impact by showcasing images first, followed by detailed text content. This is perfect for visually-driven content and portfolio presentations.",
            isBold: true,
          },
          {
            text: "Key benefits of this layout:",
            bullets: [
              { text: "Immediate visual engagement and impact" },
              { text: "Perfect for design portfolios and showcases" },
              { text: "Effective for product demonstrations" },
              { text: "Ideal for visual storytelling and presentations" },
            ],
          },
          {
            text: "This layout is particularly effective when you want to make a strong first impression with visuals and then provide comprehensive details to support the visual presentation.",
            buttons: [
              { text: "Start Project", variant: "primary" },
              { text: "Learn More", variant: "secondary" },
            ],
          },
        ],
        images: ["/public/vercel.svg", "/public/next.svg"],
        layout: "image-top",
      },
      {
        title: "Complex Content with Multiple Elements",
        paragraphs: [
          {
            text: "This section demonstrates how you can combine multiple content elements within a single section to create rich, comprehensive content experiences.",
            isBold: true,
          },
          {
            text: "You can include various content types:",
            bullets: [
              {
                text: "Regular bullet points for simple lists",
                links: [{ text: "Simple example", url: "#simple" }],
              },
              {
                text: "Bullet points with embedded links for navigation",
                links: [
                  { text: "Documentation", url: "#docs" },
                  { text: "Tutorial", url: "#tutorial" },
                  { text: "Support", url: "#support" },
                ],
              },
              { text: "Bold text for emphasis and important information" },
              { text: "Multiple paragraphs for detailed explanations" },
            ],
          },
          {
            text: "This flexibility allows you to create content that matches your specific needs, whether it's a simple service description or a complex technical documentation.",
            links: [
              { text: "Technical specifications", url: "#specs" },
              { text: "API documentation", url: "#api" },
              { text: "Integration guide", url: "#integration" },
            ],
          },
          {
            text: "You can also include multiple call-to-action buttons to guide users to different actions based on their needs and interests.",
            buttons: [
              { text: "Get Started Now", variant: "primary" },
              { text: "View Pricing", variant: "secondary" },
              { text: "Contact Sales", variant: "secondary" },
            ],
          },
        ],
        images: ["/public/logo.png"],
        layout: "text-image",
      },
      {
        title: "Advanced Features and Capabilities",
        paragraphs: [
          {
            text: "Our modal system supports advanced features that enable you to create sophisticated, interactive content experiences.",
            isBold: true,
          },
          {
            text: "Advanced capabilities include:",
            bullets: [
              {
                text: "Dynamic content loading and real-time updates",
                links: [{ text: "Live demo", url: "#demo" }],
              },
              {
                text: "Responsive design that works on all devices",
                links: [
                  { text: "Mobile preview", url: "#mobile" },
                  { text: "Tablet preview", url: "#tablet" },
                ],
              },
              { text: "Accessibility features for inclusive design" },
              { text: "SEO optimization for better discoverability" },
              { text: "Performance optimization for fast loading" },
            ],
          },
          {
            text: "The system is designed to be flexible and extensible, allowing you to add custom functionality as your needs evolve. Whether you need simple content presentation or complex interactive experiences, our modal system can accommodate your requirements.",
            links: [
              { text: "Custom development", url: "#custom" },
              { text: "Integration services", url: "#integration" },
            ],
          },
          {
            text: "Ready to create amazing content experiences? Our team is here to help you leverage the full potential of our modal system.",
            buttons: [
              { text: "Start Building", variant: "primary" },
              { text: "Schedule Demo", variant: "secondary" },
            ],
          },
        ],
        layout: "text-only",
      },
    ],
  },

  // Default Modal (fallback)
  default1: {
    category: "Service",
    title: "Service Details",
    subtitle: "Learn more about our services.",
    sections: [
      {
        title: "About This Service",
        paragraphs: [
          {
            text: "This is a comprehensive service designed to meet your business needs.",
            bullets: [
              { text: "Professional Quality" },
              { text: "Timely Delivery" },
              { text: "Ongoing Support" },
            ],
          },
        ],
        images: ["/images/truck1.jpg"],
        layout: "text-top",
      },
    ],
  },
};

// Sample Modal Data (keeping for backward compatibility)
export const sampleModalData: ModalData = modalDataMap["app-development"];

export default siteData;
